/*
 * @providesModule ClientPropertyConstants
 *
 * new string keys should be added outside of the class,
 * getter functions should still be defined in the class
 */

/* eslint-disable no-magic-numbers */

import I18n, {_m} from 'INTL';
import Preferences from 'Preferences';
import SKZConstants from 'SKZConstants';
import {Platform, NativeModules} from 'react-native';
import {MINUTES_PER_DAY} from './SKZConstants';

const SkillzModule = NativeModules.SkillzModule;

export const PushPromptConfig = {
  FirstOpen: 'FIRST_OPEN',
  FirstGame: 'FIRST_GAME',
  SecondGame: 'SECOND_GAME',
  Default: 'DEFAULT',
};

export const SavedPaymentsConfig = {
  ApplePay: 'ApplePay',
  PayPal: 'PayPal',
  Venmo: 'Venmo',
  CreditCard: 'CreditCard',
};

export const FTUECreateProfilePosition = {
  FirstOpen: 'FIRST_OPEN',
  AfterFirstGame: 'AFTER_GAME_1',
  AfterSecondGame: 'AFTER_GAME_2',
};

export const FTUEv3SignUpPath = {
  AutoGuest: 'AUTO_GUEST',
  MobileOtp: 'MOBILE_OTP',
} as const;

export type FTUEv3SignUpPathType = (typeof FTUEv3SignUpPath)[keyof typeof FTUEv3SignUpPath];

// FTUE Development Overrides Interface
interface FTUEDevOverrides {
  ftueFeatureEnabled: boolean;
  ftueScreenVisible: boolean;
  ftueFallbackPath: string;
  ftueScreenFallbackMessage: string | null;
  ftuePathOrder: string[];
  ftuePreferredSignupMethod: string;
  ftueUseSkillzTheme: boolean;
  ftueV2EmailVerificationPrizeBonus: string;
  ftueV2LobbyTooltipMenu: boolean;
  ftueV2LobbyTooltipStore: boolean;
  ftueV2LobbyTooltipAddCash: boolean;
  ftueV2LoginUIEnabled: boolean;
  ftueV2ResultScreenMessageFallback: string;
  ftueV2ResultScreenMessageHighPercentile: string;
  ftueV2ResultScreenMessageLowPercentile: string;
  ftueV2ResultScreenMessageMediumPercentileHighEnd: string;
  ftueV2ResultScreenMessageMediumPercentileLowEnd: string;
  ftueV2ResultScreenOfferPreference: string;
  ftueV2ResultScreenPercentileLowThreshold: number;
  ftueV2ResultScreenPercentileMediumThreshold: number;
  ftueV2ResultScreenPercentileMediumUserCountThreshold: number;
}

const supportedLTOModals = ['NEW', 'HISTORICAL'];

// NEW SMS Verification
const SMS_PROMPT_MAX_PROMPTS = 'account.promptsms.maxprompts';

const SMS_PROMPT_DEPOSIT = 'account.promptsms.depositcomplete.type';
const SMS_PROMPT_DEPOSIT_HEADER = 'account.promptsms.depositcomplete.header';
const SMS_PROMPT_DEPOSIT_BODY = 'account.promptsms.depositcomplete.body';

const SMS_PROMPT_WITHDRAW = 'account.promptsms.withdrawalstart.type';
const SMS_PROMPT_WITHDRAW_HEADER = 'account.promptsms.withdrawalstart.header';
const SMS_PROMPT_WITHDRAW_BODY = 'account.promptsms.withdrawalstart.body';

const SMS_SAVE_ACCOUNT = 'sso.accountsave.sms.enabled';

//LOYALTY STATUS PROGRAM
const LOYALTY_SHOW_PROFILE_FRAMES = 'loyalty.show.profile.frames';
const LOYALTY_COMBINED_WELCOME_BACK_ENABLED = 'loyalty.combined.welcome.back.enabled';

// CLUBZ
const CLUBZ_ENABLED = 'clubz.enabled';

// Horizontal Banners Constants (will replace skills news carousel in the play tab)
const HORIZONTAL_BANNERS_ENABLED = 'horizontal.banners.enabled';

// REWARD SYSTEMS
const REWARD_SYSTEMS_ENABLED = 'daily.reward.system.enabled';
const REWARD_SYSTEMS_MODAL_TRIGGER_INTERVAL_SECONDS =
  'daily.reward.system.modal.trigger.interval.seconds';
const REWARD_SYSTEMS_COUNTDOWN_TO_ASSIGN = 'daily.reward.system.countdown.to.assign';

const REWARD_SYSTEMS_V2_ENABLED = 'daily.reward.system.v2.enabled';

// PUSH NOTIFICATIONS
const PUSH_SECONDS_BETWEEN_PROMPT = 'push.seconds.between.prompt';
const PUSH_DOUBLE_PROMPT_ENABLED = 'push.double.prompt.enabled';

// SILENT ERROR HANDLING - Suppresses "Uh Oh!" dialogs for transient network errors
const SUPPRESS_UHOH_DIALOG = 'suppress_uhoh_dialog';

// MAINTENANCE
export const MAINTENANCE_IN_PROGRESS = 'is_maintenance_active';
export const PRE_MAINTENANCE = 'should_poll_client_properties';
export const MAINTENANCE_POPUP_HEADING = 'maintenance.popup.heading';
export const MAINTENANCE_POPUP_SUBHEADING = 'maintenance.popup.subheading';

//LIVE ACTIVITY
const LIVE_ACTIVITY_ENABLED = 'live.activity.enabled';
const LIVE_ACTIVITY_MAX_DAY_LIMIT = 'live.activity.max.day.limit';

// LIVE EVENTS
const LIVE_BRACKETS_SKIP_STREAM_FLOW = 'live.brackets.skip.stream.flow';
const LIVE_BRACKETS_SHOW_STRIP_IN_BRACKET_DETAILS = 'live.brackets.show.strip.in.bracket.details';
const LIVE_BRACKETS_SHOW_STRIP_IN_LOBBY = 'live.brackets.show.strip.in.lobby';
const LIVE_BRACKETS_V1_ENABLED = 'live.brackets.v1.enabled';
const LIVE_BRACKETS_SHOW_TOAST_BEFORE_START_SECONDS =
  'live.brackets.show.toast.before.start.seconds';

// LIMITED USE TOURNAMENTS
const LIMITED_USE_JOIN_CUTOFF = 'limited.use.join.cutoff.seconds';
const LIMITED_USE_TOURNAMENT_PLAY_AGAIN_ENABLED = 'limited.use.tournament.play.again.enabled';

// TOURNAMENT CELL TEMPLATES
const TOURNAMENT_TEMPLATE_UI_V2_ENABLED = 'tournament.template.ui.v2.enabled';

// RECOMMENDED TEMPLATES v1
const RECOMMENDED_TEMPLATES_ENABLED = 'recommended.templates.enabled';
const RECOMMENDED_TEMPLATE_ENTRY_FEE_VALUE = 'recommended.template.entry.fee.value';
const RECOMMENDED_SLOT1_BADGE_ACTIVE = 'recommended.slot1.badge.active';
const RECOMMENDED_SLOT1_BADGE_TEXT = 'recommended.slot1.badge.text';
const RECOMMENDED_SLOT1_RECOMMENDATION = 'recommended.slot1.recommendation';
const RECOMMENDED_SLOT2_BADGE_ACTIVE = 'recommended.slot2.badge.active';
const RECOMMENDED_SLOT2_BADGE_TEXT = 'recommended.slot2.badge.text';
const RECOMMENDED_SLOT2_RECOMMENDATION = 'recommended.slot2.recommendation';
const RECOMMENDED_SLOT3_BADGE_ACTIVE = 'recommended.slot3.badge.active';
const RECOMMENDED_SLOT3_BADGE_TEXT = 'recommended.slot3.badge.text';
const RECOMMENDED_SLOT3_RECOMMENDATION = 'recommended.slot3.recommendation';

// RECOMMENDED TEMPLATES v2
const RECOMMENDED_TEMPLATES_V2_UI_ENABLED = 'recommended_v2.ui.enabled';
const RECOMMENDED_TEMPLATES_V2_LIVE_EVENT_ENABLED = 'recommended_v2.liveevent.enabled';
const RECOMMENDED_TEMPLATES_V2_ASCENDING_ENABLED = 'recommended_v2.ascending.enabled';
const RECOMMENDED_TEMPLATES_V2_SLOT1_BADGE_TEXT = 'recommended_v2.slot1.badge.text';
const RECOMMENDED_TEMPLATES_V2_SLOT2_BADGE_TEXT = 'recommended_v2.slot2.badge.text';
const RECOMMENDED_TEMPLATES_V2_SLOT3_BADGE_TEXT = 'recommended_v2.slot3.badge.text';
const RECOMMENDED_TEMPLATES_V2_ENTRY_FEE_VALUE = 'recommended_v2.entry.fee.value';

// LEAGUES
const LEAGUES_STATUS_INTERSTITIAL_ENABLED = 'leagues.status.interstitial.enabled';
const LEAGUES_STATUS_INTERSTITIAL_MAX_SKIPS = 'leagues.status.interstitial.max.skips';
const LEAGUES_MANUAL_CLAIM_ENABLED = 'leagues.manual.claim.enabled';
const LEAGUES_PAID_LEAGUE_PROMPT_COOLDOWN_MINUTES = 'leagues.paid.league.prompt.cooldown.minutes';
const LEAGUES_PAID_LEAGUE_PROMPT_MAX_TRIGGERS_SEASON =
  'leagues.paid.league.prompt.max.triggers.season';
const LEAGUES_PAID_LEAGUE_OPT_IN_CUTOFF_MIN = 'leagues_paid_league_opt_in_cutoff_min'; // game property
const LEAGUES_V3_UI_V2_ENABLED = 'leagues_v3_ui_v2_enabled'; // game property

// H2H RESULTS EXPERIMENT
const H2H_RESULTS_EXPERIMENT_ENABLED = 'h2h.results.experiment.enabled';
const H2H_RESULTS_REMATCH_EXPERIMENT_ENABLED = 'h2h.results.rematch.experiment.enabled';
const H2H_RESULTS_RECOMMENDED_TOURNAMENT_ENABLED = 'h2h.results.recommended.tournament.enabled';
const H2H_RESULTS_RECOMMENDED_TOURNAMENT_ENABLED_COUNT =
  'h2h.results.recommended.tournament.enabled.count';
const H2H_RESULTS_ADD_BALANCE_CTA_ENABLED = 'h2h.results.add.balance.cta.enabled';
const H2H_RESULTS_HUGE_WIN_ENABLED = 'h2h.results.huge.win.enabled';
const H2H_RESULTS_MARGINAL_LOSS_ENABLED = 'h2h.results.marginal.loss.enabled';
const H2H_RESULTS_TROPHIES_ENABLED = 'h2h.results.trophies.enabled';
const H2H_RESULTS_SPECTATE_ENABLED = 'h2h.results.spectate.enabled';

// H2H MAX ELIGIBLE ENTRY FEE
const ENABLE_H2H_MAX_ELIGIBLE_ENTRY_FEE = 'enable_h2h_max_eligible_entry_fee'; // game property
const H2H_MAX_ELIGIBLE_ENTRY_FEE_CEILING = 'h2h.max.eligible.entry.fee.ceiling';

// ABUSE PREVENTION
const LOGOUT_ENABLED = 'logout.enabled';

// CASH GAME REGISTRATION
const SKIP_CASH_GAME_FULL_REG_CHECK = 'skip_cash_game_full_reg_check';

//Instant Match
const INSTANT_MATCH_ENABLED = 'instantmatch.enabled';
const INSTANT_MATCH_ENTRY_FEE_BAND = 'instantmatch.entryfeeband';
const INSTANT_MATCH_ENTRY_FEE_THRESHOLD = 'instantmatch.entryfeethreshold';
const INSTANT_MATCH_ENTRY_FEE_VALUE = 'instantmatch.entryfeevalue';
//Bonus Cash Restriction
const BONUS_CASH_RESTRICTION_UI_V1_ENABLED = 'bonus.cash.restriction.v1.enabled';

//Loyalty store v2 revamp
const LOYALTY_REVAMP_UI_ENABLED = 'loyalty.store.v2.ui.revamp.enabled';
const VIP_CONTACT_NUMBER = 'vip.contact.number';
const VIP_CONTACT_AGENT_NAME = 'vip.contact.agent.name';
const VIP_CONTACT_EMAIL = 'vip.contact.email';
const VIP_CONTACT_VCF_URL = 'vip.contact.vcf.url';
const VIP_CONTACT_WHATSAPP_URL = 'vip.contact.whatsapp.url';

// Withdrawal Constants
const PLAYER_WITHDRAWAL_METHOD = 'playerWithdrawal.method';
const PLAYER_WITHDRAWAL_PAYPAL_ENABLED = 'playerWithdrawal.paypal.enabled';
const PLAYER_WITHDRAWAL_PAYPAL_MAX_AMOUNT = 'playerWithdrawal.amount';
const PLAYER_WITHDRAWAL_AEROPAY_ENABLED = 'playerWithdrawal.aeropay.enabled';
const PLAYER_WITHDRAWAL_AEROPAY_RTP_ENABLED = 'playerWithdrawal.aeropay.rtp.enabled';
const PLAYER_WITHDRAWAL_SIDE_MENU_BUTTONS_ENABLED = 'playerWithdrawal.side.menu.buttons.enabled';
const PLAYER_WITHDRAWAL_AEROPAY_LINK_BANK_POPUP_ENABLED =
  'playerWithdrawal.aeropay.link.bank.popup.enabled';
const PLAYER_WITHDRAWAL_AEROPAY_BANK_POPUP_INTERVAL_MINUTES =
  'playerWithdrawal.aeropay.bank.popup.interval.minutes';
const PLAYER_WITHDRAWAL_CANCELLATION_ALL_ENABLED = 'playerWithdrawal.cancellation.all.enabled';
const PLAYER_WITHDRAWAL_AEROPAY_RTP_FEE_ENABLED = 'playerWithdrawal.aeropay.rtp.fee.enabled';
const PLAYTHROUGH_ENABLED = 'playthrough.enabled';
const PLAYTHROUGH_MULTIPLIER = 'playthrough.multiplier';

// Store Page Revamp
const STORE_V2_ENABLED = 'store.v2.enabled';
const STORE_V2_DEPOSIT_SLATE_VARIANT = 'store.v2.deposit.slate.variant';
const STORE_V2_CAROUSEL_BIGGER_ENABLED = 'store.v2.carousel.bigger.enabled';
const STORE_V2_CAROUSEL_LTO_FULL_SHOW = 'store.v2.carousel.lto.full.show';
const STORE_V2_CAROUSEL_LEGACY_ENABLED = 'store.v2.carousel.legacy.enabled';
const STORE_V2_DEPOSIT_SUCCESS_RECOMMENDED_TOURNAMENT =
  'store.v2.deposit.success.recommended.tournament';
const STORE_V2_DEPOSIT_SUCCESS_ENABLED = 'store.v2.deposit.success.enabled';
const STORE_V2_CAROUSEL_EXTRA_VALUE_BADGE_ENABLED = 'store.v2.carousel.extra.value.badge.enabled';

// MIGRATE USER PROPERTIES
const MIGRATE_USER_ENABLED = 'migrate-user';
const MIGRATE_BANNER_URL = 'migrate_banner_url';
const MIGRATE_IOS_URL = 'migrate_ios_url';
const MIGRATE_ANDROID_URL = 'migrate_android_url';
const APPLE_PAY_ENABLED = 'checkout.apple.pay.modal.enabled';

// Live Brackets
const TOURNAMENT_TIMER_LENGTH_SECONDS = 'tournament_timer_length_seconds';

// Lobby/PlayTab Banners
const BANNER_LOBBY_GAME_IDS = 'banner.lobby.game.ids';
const BANNER_LOBBY_TARGET_URLS = 'banner.lobby.target.urls';
const BANNER_LOBBY_IMAGE_URLS = 'banner.lobby.image.urls';
const BANNER_LOBBY_POSITIONS = 'banner.lobby.positions';

// History Screen Banners
const BANNER_HISTORY_GAME_IDS = 'banner.history.game.ids';
const BANNER_HISTORY_TARGET_URLS = 'banner.history.target.urls';
const BANNER_HISTORY_IMAGE_URLS = 'banner.history.image.urls';
const BANNER_HISTORY_POSITIONS = 'banner.history.positions';

// Banner Control
const SHOW_BANNER_EXTERNAL = 'show_banner_external';

// FTUE 2.0
const FTUE_V3_ENABLED = 'ftue_v3.enabled';
const FTUE_V3_SIGNUP_PATH = 'ftue_v3.signup.path';
const FTUE_V3_INTERSTITIALS_SKIP_INITIAL_LOAD = 'ftue_v3.interstitials.skip.initial.load';

class ClientPropertyConstants {
  VS_COUNTDOWN_TIME: string;
  VS_SKIP_TIME: string;
  VS_Z_PLAY_AGAIN_MAX: string;
  VS_CASH_PLAY_AGAIN_MAX: string;
  VS_SYNC_PLAY_AGAIN_MAX: string;
  VS_SYNC_PLAY_AGAIN_MAX_Z: string;
  LOCAL_NOTIF_ABORT_TIME: string;
  PUSH_PROMPT_CONFIG: string;
  LEGAL_URL: string;
  REWARD_CALENDAR_ENABLED: string;
  REFERRAL_V2_ENABLED: string;
  REFERRAL_SIDE_MENU_BUTTON_COPY: string;
  REFERRAL_STORE_BUTTON_ENABLED: string;
  REFERRAL_STORE_BUTTON_COPY: string;
  REFERRAL_NEW_UI_WITH_EARNINGS: string;
  REFERRAL_NEW_UI_WAGERING_PERCENTAGE: string;
  REFERRAL_NEW_UI_TOTAL_BONUS_CASH: string;
  REFERRAL_NEW_UI_SHARE_TEXT: string;
  REFERRAL_NEW_UI_STORE_CASH_THRESHOLD: string;
  IN_APP_NOTIF_DISPLAY_INTERVAL: string;
  IN_APP_NOTIF_DISPLAY_LENGTH: string;
  WB_DEFAULT_TO_OPEN: string;
  WB_ANIMATION_ENABLED: string;
  WB_CELL_OPEN_ALL_VIBRATION: string;
  WB_ANIMATION_SPEED: string;
  WB_CELL_OPEN_ONE_VIBRATION: string;
  WB_FLOW_ENABLED: string;
  WB_DAILY_CHALLENGES_ENABLED: string;
  LIVE_EVENT_FTUE_TIMEFRAME_SECONDS: string;
  LIVE_EVENT_JOIN_CUTOFF: string;
  LIVE_EVENT_CREATION_ENABLED: string;
  VIRTUAL_CURRENCY_ONLY: string;
  SYNC_TIEBREAKER_MAX_TIME: string;
  SYNC_OPPONENT_NUM_VIBRATIONS: string;
  SYNC_LIVE_MATCH_CONFIRMATION: string;
  SYNC_CELL_ENABLED: string;
  SYNC_MIN_PING_STRENGTH: string;
  SYNC_STRONG_PING_STRENGTH: string;
  SYNC_PING_AVERAGE_COUNT: string;
  SYNC_PING_CHECK_INTERVAL: string;
  SYNC_VERSUS_SEARCHING_TIME: string;
  SYNC_MAX_WAIT_TIME: string;
  SYNC_NOTIF_BACKGROUND_TITLE: string;
  SYNC_NOTIF_OPPONENT_TITLE: string;
  SYNC_NOTIF_BACKGROUND_BODY: string;
  SYNC_NOTIF_OPPONENT_BODY: string;
  SYNC_VERSUS_HANDSHAKE: string;
  SYNC_VERSUS_EST_TIME_MIN: string;
  SYNC_VERSUS_EST_TIME_MAX: string;
  SYNC_VERSUS_EST_TIME_MESSAGE: string;
  SYNC_VERSUS_NOTIF_PROMPT_INTERVAL: string;
  SYNC_VS_OPPONENT_MODAL: string;
  SYNC_VS_BEGIN_COUNTDOWN: string;
  SYNC_VS_TIES_BEGIN_COUNTDOWN: string;
  SYNC_VERSUS_SEARCHING_INTERVAL: string;
  SYNC_TIES_TRANSITION_SCREEN: string;
  SYNC_MATCHMAKING_ALLOW_SLEEP: string;
  SYNC_FTUE_GAMES_REQUIRED: string;
  SYNC_FTUE_MAX_WAIT_TIME: string;
  SYNC_1vN_ENABLED: string;
  FTUE_TYPE: string;
  SYNC_FTUE_CHECK_MAX_WAIT_TIME_CLIENT: string;
  SYNC_FTUE_TRAINER_BOT_CARD_ENABLED: string;
  FTUE_PROFILE_ORDER: string;
  IS_SHOW_FTUE_CASH_MATCHES: string;
  IS_ASYNC_FTUE_BOT_ENABLED: string;
  FTUE_SPLIT_ACCOUNT_CREATION_ENABLED: string;
  SKIP_HOME_FTUE: string;
  FTUE_SKIP_MATCH_TUTORIAL: string;
  FTUE_SLIDESHOW_V2_ENABLED: string;
  FTUE_SLIDESHOW_SLIDE_AMOUNT: string;
  FTUE_SLIDESHOW_SLIDE1_BODY: string;
  FTUE_SLIDESHOW_SLIDE1_BACKGROUND: string;
  FTUE_SLIDESHOW_SLIDE2_BODY: string;
  FTUE_SLIDESHOW_SLIDE2_HEADER: string;
  FTUE_SLIDESHOW_SLIDE2_BACKGROUND: string;
  FTUE_SLIDESHOW_SLIDE3_BODY: string;
  FTUE_SLIDESHOW_SLIDE3_HEADER: string;
  FTUE_SLIDESHOW_SLIDE3_BACKGROUND: string;
  FTUE_SLIDESHOW_SLIDE4_BODY: string;
  FTUE_SLIDESHOW_SLIDE4_HEADER: string;
  FTUE_SLIDESHOW_SLIDE4_BACKGROUND: string;
  FTUE_FEATURE_ENABLED: string;
  FTUE_SCREEN_VISIBLE: string;
  FTUE_FALLBACK_PATH: string;
  FTUE_SCREEN_FALLBACK_MESSAGE: string;
  FTUE_PATH_ORDER: string;
  FTUE_PREFERRED_SIGNUP_METHOD: string;
  FTUE_USE_SKILLZ_THEME: string;
  FTUE_V2_EMAIL_VERIFICATION_PRIZE_BONUS: string;
  FTUE_V2_LOBBY_TOOLTIP_MENU: string;
  FTUE_V2_LOBBY_TOOLTIP_STORE: string;
  FTUE_V2_LOBBY_TOOLTIP_ADD_CASH: string;
  FTUE_V2_LOGIN_UI_ENABLED: string;
  FTUE_V2_RESULT_SCREEN_MESSAGE_FALLBACK: string;
  FTUE_V2_RESULT_SCREEN_MESSAGE_HIGH_PERCENTILE: string;
  FTUE_V2_RESULT_SCREEN_MESSAGE_LOW_PERCENTILE: string;
  FTUE_V2_RESULT_SCREEN_MESSAGE_MEDIUM_PERCENTILE_HIGH_END: string;
  FTUE_V2_RESULT_SCREEN_MESSAGE_MEDIUM_PERCENTILE_LOW_END: string;
  FTUE_V2_RESULT_SCREEN_OFFER_PREFERENCE: string;
  FTUE_V2_RESULT_SCREEN_PERCENTILE_LOW_THRESHOLD: string;
  FTUE_V2_RESULT_SCREEN_PERCENTILE_MEDIUM_THRESHOLD: string;
  FTUE_V2_RESULT_SCREEN_PERCENTILE_MEDIUM_USER_COUNT_THRESHOLD: string;
  PLAYER_CHAT_ENTRYPOINT_VERSION: string;
  PLAYER_CHAT_ENABLED: string;
  PLAYER_CHAT_BAR_MESSAGE_DISPLAY: string;
  PLAYER_CHAT_BAR_REFRESH_INTERVAL: string;
  PLAYER_CHAT_BAR_FETCHED_MESSAGES: string;
  PLAYER_CHAT_BLOCKED_LIMIT: string;
  PLAYER_CHAT_MUTED_LIMIT: string;
  PLAYER_CHAT_CONTACTS_LIMIT: string;
  PLAYER_CHAT_ROOM_CHAR_DISPLAY_LIMIT: string;
  PLAYER_CHAT_PHONE_VERIFY_PROMPT_INTERVAL: string;
  PLAYER_CHAT_MESSAGE_RATE_LIMIT: string;
  PLAYER_CHAT_RATE_LIMIT_TIME_FRAME: string;
  PLAYER_CHAT_RATE_LIMIT_MIN_DISPLAY: string;
  PLAYER_CHAT_MESSAGE_CHAR_LIMIT: string;
  PLAYER_CHAT_COPY_CONFIRM_DURATION: string;
  PLAYER_CHAT_MUTED_DMS_SHOWN: string;
  PLAYER_CHAT_DM_MAX_PEOPLE: string;
  PLAYER_CHAT_PHONE_VERIFY_IMAGE: string;
  PLAYER_CHAT_PHONE_VERIFY_HEADER_COPY: string;
  PLAYER_CHAT_PHONE_VERIFY_PROMPT_COPY: string;
  PLAYER_CHAT_PHONE_VERIFY_NUMBER_INPUT: string;
  PLAYER_CHAT_PHONE_VERIFY_NUMBER_CTA: string;
  PLAYER_CHAT_PHONE_VERIFY_CODE_INPUT: string;
  PLAYER_CHAT_PHONE_VERIFY_CODE_CTA: string;
  PLAYER_CHAT_PROFILE_REQUEST_LIMIT: string;
  PLAYER_CHAT_CONTACTS_POLLING: string;
  PLAYER_CHAT_CONTACTS_UPDATE_INTERVAL_1: string;
  PLAYER_CHAT_CONTACTS_UPDATE_INTERVAL_2: string;
  PLAYER_CHAT_CONTACTS_UPDATE_SIZE: string;
  PLAYER_CHAT_MESSAGES_LOADED_COUNT: string;
  PLAYER_CHAT_ROOM_RECONNECT_TIME: string;
  PLAYER_CHAT_ROOM_RECONNECT_TOLERANCE: string;
  PLAYER_CHAT_ROOM_RECONNECT_MAX: string;
  PLAYER_CHAT_APP_WIDE_UNREAD_INTERVAL: string;
  PLAYER_CHAT_UNREAD_INTERVAL: string;
  PLAYER_CHAT_DO_NOT_PROMPT_DAYS: string;
  PLAYER_CHAT_FTUE_GAMES_REQUIRED: string;
  PLAYER_CHAT_FTUE_SWIPE_GESTURES: string;
  PLAYER_CHAT_FTUE_GROUP_CHAT_OPEN: string;
  PLAYER_CHAT_FTUE_ENABLED: string;
  PLAYER_CHAT_SIDEBAR_V2_ENABLED: string;
  PLAYER_CHAT_OPEN_SIDEBAR_FIRST: string;
  PLAYER_CHAT_FRIEND_SEARCH_LIMIT: string;
  PLAYER_CHAT_CONTACTS_BATCH_SIZE: string;
  PLAYER_CHAT_TEMP_BAN: string;
  PLAYER_CHAT_TEMP_MUTE: string;
  PLAYER_CHAT_MOD_COMMENT_MIN: string;
  PLAYER_CHAT_MOD_COMMENT_MAX: string;
  GDPR_OPT_IN_LANGUAGE: string;
  GDPR_ENABLED: string;
  PLAYER_LEVELS_ENABLED: string;
  HOMESCREEN_OPENING_SCREEN: string;
  HOME_STATUS_TICKETZ_NAVIGATION: string;
  MATCH_CODE_VISIBILITY_ENABLED: string;
  SKILLZ_PROMOTION_ENABLED: string;
  THREE_PLUS_H2H_STYLE_V2_ENABLED: string;
  GAME_OPERATIONS_CONTAINER_ENABLED: string;
  GAME_OPERATIONS_LOCKED_EVENT_BUTTON_ENABLED: string;
  GAME_OPERATIONS_TUTORIAL_ENABLED: string;
  GAME_OPERATIONS_TUTORIAL_IMAGE_URL: string;
  GAME_OPERATIONS_REFERRAL_IMAGE_URL: string;
  FRIENDS_TOURNAMENTS_INCENTIVES_MAX: string;
  FRIENDS_TOURNAMENTS_PLAYER_ENABLED_FOR_USER: string;
  FRIENDS_TOURNAMENTS_PLAYER_ENABLED: string;
  FRIENDS_TOURNAMENTS_NUDGE_MINUTES_REMAINING: string;
  FRIENDS_TOURNAMENTS_NUDGE_MINUTES_COOL_DOWN: string;
  FRIENDS_TOURNAMENTS_CONTACT_WARNING_ENABLED: string;
  FRIENDS_TOURNAMENTS_CONSENT_MESSAGE: string;
  FRIENDS_TOURNAMENTS_INVITE_METHOD: string;
  FRIENDS_TOURNAMENTS_INVITE_ENABLED: string;
  FRIENDS_TOURNAMENTS_MEDALS_ENABLED: string;
  FRIENDS_TOURNAMENTS_PHONE_NUMBER: string;
  FRIENDS_TOURNAMENTS_OFFSYSTEM_FRIENDS_TILE_COPY: string;
  FRIENDS_TOURNAMENTS_FREE_ENTRY_BANNER: string;
  FRIENDS_TOURNAMENTS_RECOMMENDED_MINUTES: string;
  FRIENDS_TOURNAMENTS_NUDGE_METHOD: string;
  FRIENDS_TOURNAMENTS_NUDGE_MINUTES_REMAINING_OFFSYSTEM: string;
  FRIENDS_TOURNAMENTS_NUDGE_MINUTES_COOL_DOWN_OFFSYSTEM: string;
  FRIENDS_TOURNAMENTS_OFFSYSTEM_CRITERIA: string;
  FRIENDS_TOURNAMENTS_OFF_SYSTEM_ACCOUNT_CREATION_MINUTES: string;
  FRIENDS_TOURNAMENTS_GAME_ACCOUNTS: string;
  FRIENDS_TOURNAMENTS_RATINGS_ENABLED: string;
  SAVE_ACCOUNT_BADGE_GAMES_REQUIRED: string;
  SAVE_ACCOUNT_BADGE_MINUTES: string;
  VERIFY_PHONE_BADGE_GAMES_REQUIRED: string;
  VERIFY_PHONE_BADGE_MINUTES: string;
  FRIENDS_TOURNAMENTS_OFFSYSTEM_INVITE_COPY: string;
  FRIENDS_TOURNAMENTS_RECOMMENDED_INVITE_COPY: string;
  RATING_PROMPT_TICKETZ_THRESHOLD: string;
  RATING_PROMPT_Z_TROPHY_THRESHOLD: string;
  RATING_PROMPT_CASH_TROPHY_THRESHOLD: string;
  RATING_PROMPT_TICKETZ_TROPHY_THRESHOLD: string;
  RATING_PROMPT_TICKETZ_REWARD_IDS: string;
  RATING_PROMPT_TROPHY_IDS: string;
  RATING_PROMPT_AFTER_WIN_SESSION_SECS: string;
  RATING_PROMPT_AFTER_WIN_WIN_RATE: string;
  RATING_PROMPT_AFTER_WIN_INSTALL_TIME: string;
  RATING_PROMPT_INSTALL_SECONDS: string;
  RATING_PROMPT_REMIND_SECONDS: string;
  RATING_PROMPT_EVENTS_REQUIRED: string;
  RATING_PROMPT_SESSIONS_PER_PROMPT: string;
  RATING_PROMPT_WEEKLY_SESSIONS_PER_PROMPT: string;
  SKILLZ_NEWS_ENABLED: string;
  SKILLZ_NEWS_BANNER_LOCATION: string;
  SKILLZ_NEWS_BANNER_COUNT: string;
  SKILLZ_NEWS_CHANNEL_NAME: string;
  SKILLZ_NEWS_SCROLLABLE: string;
  POOLED_LIQUIDITY_TILE_COPY: string;
  POOLED_LIQUIDITY_TILE_COPY_SELECTED: string;
  POOLED_LIQUIDITY_STATES_SELECTOR_COPY: string;
  SYNC_FASTMATCH_HEADER_COPY: string;
  SYNC_FASTMATCH_SUBHEADER_COPY: string;
  SYNC_FASTMATCH_V2_ENABLED: string;
  FAST_MATCH_TIPS_ENABLED: string;
  FASTMATCH_V2_INFO_HEADER: string;
  FASTMATCH_V2_INFO_BODY: string;
  SYNC_FASTMATCH_ACCEPT_MATCH_COPY: string;
  SYNC_FASTMATCH_STAKE_EMPTY_COPY: string;
  FAST_MATCH_CONFIRMATION_TIME: string;
  STORE_PLAYERS_HAVE_WON: string;
  STORE_DEPOSIT_COPY: string;
  STORE_WITHDRAW: string;
  LOGIN_BONUS_ENABLED: string;
  TICKETZ_STORE_CAROUSE_ENABLED: string;
  TROPHIES_BUTTON_ENABLED: string;
  OUT_OF_FUNDS_DEPOSIT_MODAL: string;
  LOW_Z_BALANCE_LTO_ENABLED: string;
  LOW_Z_BALANCE_LTO_ID: string;
  TROPHIES_PAGE_VERSION: string;
  PUSH_NOTIFICATIONS_SETTINGS_ENABLED: string;
  ACCOUNT_DELETION_ENABLED: string;
  ACCOUNT_DELETION_MODAL_COPY: string;
  CHECKOUT_DETAILS_SHOW_BONUS: string;
  CHECKOUT_DETAILS_SHOW_NEW_BALANCE: string;
  CHECKOUT_BILLING_ADDRESS_REQUIRED: string;
  CHECKOUT_DEFAULT_APPLEPAY: string;
  CHECKOUT_ENABLE_CREDITCARD_SCAN: string;
  CHECKOUT_V2_PAYPAL_USE_BRAINTREE: string;
  CHECKOUT_VENMO_ENABLED: string;
  CHECKOUT_VENMO_BANNER: string;
  CHECKOUT_VENMO_MODAL: string;
  CHECKOUT_VENMO_ENABLED_HIGHLIGHTED: string;
  CHECKOUT_ACH_DEPOSIT_ENABLED: string;
  CHECKOUT_ALLOW_DEPOSIT_DURING_WITHDRAWAL: string;
  CHECKOUT_ACH_SECURITY_MESSAGING_ENABLED: string;
  ADDFUNDS_LTO_MODAL: string;
  LTO_ON_PLAY: string;
  LTO_ON_STORE: string;
  PCI_EPROTECT_ENABLED: string;
  PCI_TRANSACT_WITHOUT_THREATMETRIX: string;
  THIRD_PARTY_ADS_ENABLED: string;
  THIRD_PARTY_ADS_ENABLED_WITH_INTERSTITIALS: string;
  THIRD_PARTY_ADS_DOUBLE_WINNING_RECOVER_LOSS_MODAL_RESET: string;
  THIRD_PARTY_ADS_WITH_REWARD: string;
  THIRD_PARTY_ADS_WITHOUT_REWARD: string;
  THIRD_PARTY_ADS_HOUSE_ADS_PERCENTAGE: string;
  STORE_CLAIMZ_ENABLED: string;
  FTUE_MATCH_CODE_FOCUSED: string;
  FTUE_MATCH_CODE_FRIENDS: string;
  PROGRESSION_MODAL_ENABLED: string;
  LOYALTY_ENABLED: string;
  SKILLZ_REWARDS_CELL_ENABLED: string;
  TICKETZ_TIER_USE_SCALABLE_IMAGE: string;
  TICKETZ_TIER_BADGES_ENABLED: string;
  IDFA_FTUE_PROMPT_SETTNG: string;
  IDFA_DEPOSIT_PROMPT_ENABLED: string;
  IDFA_FIRST_OPEN_PLAY: string;
  LIVE_EVENT_HOSTED_ENABLED: string;
  AUTO_EXPAND_PRO_H2H_AFTER_X_MATCHES: string;
  AUTO_EXPAND_LIVE_TOURNAMENT_TEMPLATES_AFTER_X_MATCHES: string;
  DEV_PROGRESSION_HOME_ENABLED: string;
  DEV_PROGRESSION_SIDEBAR_ENABLED: string;
  FTUE_WEB_TUTORIAL_SCREEN_ENABLED: string;
  FTUE_CURRENCY_PROMPT_ENABLED: string;
  WELCOME_BACK_V3_ENABLED: string;
  H2H_RESULTS_EXPERIMENT_ENABLED: string;
  H2H_RESULTS_REMATCH_EXPERIMENT_ENABLED: string;
  H2H_RESULTS_RECOMMENDED_TOURNAMENT_ENABLED: string;
  H2H_RESULTS_RECOMMENDED_TOURNAMENT_ENABLED_COUNT: string;
  H2H_RESULTS_ADD_BALANCE_CTA_ENABLED: string;
  H2H_RESULTS_HUGE_WIN_ENABLED: string;
  H2H_RESULTS_MARGINAL_LOSS_ENABLED: string;
  H2H_RESULTS_TROPHIES_ENABLED: string;
  H2H_RESULTS_SPECTATE_ENABLED: string;
  LEAGUES_VERSION: string;
  LEAGUES_TOAST_ENABLED: string;
  LEAGUES_NEGATIVE_TOAST_ENABLED: string;
  LEAGUES_COMBINED_MEDALS_ENABLED: string;
  LEAGUES_LOCKED_FTUE_V2_ENABLED: string;
  LEAGUES_V3_MEDAL_LEADERBOARD_ENABLED: string;
  RESULT_NEXT_BUTTON_ENABLED: string;
  VERIFY_SMS_SAVEACCOUNT: string;
  VERIFY_SMS_GAMEPLAY: string;
  VERIFY_SMS_DEPOSIT: string;
  VERIFY_SMS_CLOSE_BUTTON: string;
  FEDLOGIN_GOOGLE_IOS_LOGIN: string;
  FEDLOGIN_GOOGLE_ANDROID_LOGIN: string;
  FEDLOGIN_APPLE_IOS_LOGIN: string;
  FEDLOGIN_APPLE_ANDROID_LOGIN: string;
  FEDLOGIN_FACEBOOK_IOS_LOGIN: string;
  FEDLOGIN_FACEBOOK_ANDROID_LOGIN: string;
  FEDLOGIN_GOOGLE_IOS_SIGNUP: string;
  FEDLOGIN_GOOGLE_ANDROID_SIGNUP: string;
  FEDLOGIN_APPLE_IOS_SIGNUP: string;
  FEDLOGIN_APPLE_ANDROID_SIGNUP: string;
  FEDLOGIN_FACEBOOK_IOS_SIGNUP: string;
  FEDLOGIN_FACEBOOK_ANDROID_SIGNUP: string;
  SSO_LOGIN_SMS_ENABLED: string;
  SSO_LOGIN_EMAIL_ENABLED: string;
  SSO_LOGIN_EMAIL_DEFAULT: string;
  CORELOOP_TUNGSTEN_ENABLED: string;
  CORELOOP_TUNGSTEN_LITE_ENABLED: string;
  RUM_ENABLED: string;
  IS_ERROR_HANDLER_ENABLED: string;
  IS_ERROR_BOUNDARY_ENABLED: string;
  RUM_SESSION_SAMPLE_RATE: string;
  RUM_NATIVE_CRASH_DETECTION: string;
  RUM_RESOURCE_SAMPLE_RATE: string;
  SPECTATE_ENABLED_RESULTS: string;
  SKIP_CASH_GAME_FULL_REG_CHECK: string;
  MAINTENANCE_IN_PROGRESS: string;
  PRE_MAINTENANCE: string;
  MAINTENANCE_POPUP_HEADING: string;
  MAINTENANCE_POPUP_SUBHEADING: string;
  AVATAR_RANDOMIZER_ENABLED: string;
  AVATAR_RANDOMIZER_ONLY_FTUE: string;
  USERNAME_RANDOMIZER_ENABLED: string;
  FTUE_REVAMP_ENABLED: string;
  COMPRESSED_BIRTHDAY_OPT_IN: string;
  MATCH_TEMPLATE_FTUE_ENABLED: string;
  EVENT_CHAT_ENABLED: string;
  LIVE_BRACKET_EVENT_CHAT_ENABLED: string;
  LIVE_BRACKET_PREVENT_JOIN_SECS: string;
  SHOW_MORE_TEXT_BUTTONS_ENABLED: string;
  INTERSTITIALS_ENABLED: string;
  INTERSTITIALS_HOUSEADS_ENABLED: string;
  INTERSTITIALS_GLOBAL_COOLDOWN: string;
  DAILY_BONUS_CASH_SCREEN_COOLDOWN: string;
  DAILY_BONUS_CASH_SCREEN_MAX_REDIRECT_COUNT: string;
  FTUE_CONVERSIONS_ENABLED: string;
  TUTORIAL_TRAINERS_IMAGE_SET: string;
  TOURNAMENT_TIMER_LENGTH_SECONDS: string;

  constructor() {
    // Assorted constants
    this.VS_COUNTDOWN_TIME = 'vs.tickertime';
    this.VS_SKIP_TIME = 'vs.endseconds';
    this.VS_Z_PLAY_AGAIN_MAX = 'vs.z_play_again.max';
    this.VS_CASH_PLAY_AGAIN_MAX = 'vs.play_again.max';
    this.VS_SYNC_PLAY_AGAIN_MAX = 'vs.sync_play_again.max';
    this.VS_SYNC_PLAY_AGAIN_MAX_Z = 'vs.sync_play_again.max.z';
    this.LOCAL_NOTIF_ABORT_TIME = 'local.notification.abort.time.remaining';
    this.PUSH_PROMPT_CONFIG = 'push.prompt';
    this.LEGAL_URL = 'legal.website.url';
    this.REWARD_CALENDAR_ENABLED = 'reward.calendar.enabled.v1.2';

    // Referral
    this.REFERRAL_V2_ENABLED = 'referral.v2.enabled';
    this.REFERRAL_SIDE_MENU_BUTTON_COPY = 'referral.hamburger.copy';
    this.REFERRAL_STORE_BUTTON_ENABLED = 'referral.store.entry.enabled';
    this.REFERRAL_STORE_BUTTON_COPY = 'referral.store.entry.copy';
    this.REFERRAL_NEW_UI_WITH_EARNINGS = 'referral.new.ui.with.earnings';
    this.REFERRAL_NEW_UI_WAGERING_PERCENTAGE = 'referral.new.ui.wagering_percentage';
    this.REFERRAL_NEW_UI_TOTAL_BONUS_CASH = 'referral.new.ui.total.bonus.cash';
    this.REFERRAL_NEW_UI_SHARE_TEXT = 'referral.new.ui.share.text';
    this.REFERRAL_NEW_UI_STORE_CASH_THRESHOLD = 'referral.new.store.cash.threshold';
    // In App Notifications
    this.IN_APP_NOTIF_DISPLAY_INTERVAL = 'inAppNotifications.displayInterval';
    this.IN_APP_NOTIF_DISPLAY_LENGTH = 'inAppNotifications.displayLength';

    // Welcome Back constants
    this.WB_DEFAULT_TO_OPEN = 'welcomeBack.defaultToOpen';
    this.WB_ANIMATION_ENABLED = 'welcomeBack.animation.enabled';
    this.WB_CELL_OPEN_ALL_VIBRATION = 'welcomeBack.cells.openAllVibrationCount';
    this.WB_ANIMATION_SPEED = 'welcomeBack.animation.animationSpeed';
    this.WB_CELL_OPEN_ONE_VIBRATION = 'welcomeBack.cells.singleCellVibrationCount';

    this.WB_FLOW_ENABLED = 'welcome.back.flow.enabled';
    this.WB_DAILY_CHALLENGES_ENABLED = 'welcome.back.daily.challenges.prompt.enabled';

    // Live Event FTUE Constants
    this.LIVE_EVENT_FTUE_TIMEFRAME_SECONDS = 'live_event_ftue_timeframe_seconds';

    // Live Event constants
    this.LIVE_EVENT_JOIN_CUTOFF = 'live_event.join.cutoff.seconds';
    this.LIVE_EVENT_CREATION_ENABLED = 'live.event.creation.enabled';

    // Live Bracket constants
    this.LIVE_BRACKET_PREVENT_JOIN_SECS = 'live_bracket_prevent_join_secs';

    // Virtual Currency constants
    this.VIRTUAL_CURRENCY_ONLY = 'virtual_currency_only';

    // Sync Play constants
    this.SYNC_TIEBREAKER_MAX_TIME = 'sync_max_time_allowed_for_tiebreaker_creation_millis';
    this.SYNC_OPPONENT_NUM_VIBRATIONS = 'sync.opponentFound.numVibrations';
    this.SYNC_LIVE_MATCH_CONFIRMATION = 'sync.rttb.liveMatchConfirmation';
    this.SYNC_CELL_ENABLED = 'sync.gameplay.cell.enabled';
    this.SYNC_MIN_PING_STRENGTH = 'sync_rttb_min_ping_strength_millis';
    this.SYNC_STRONG_PING_STRENGTH = 'sync_rttb_strong_ping_strength_millis';
    this.SYNC_PING_AVERAGE_COUNT = 'sync_rttb_ping_average_count';
    this.SYNC_PING_CHECK_INTERVAL = 'sync_rttb_ping_check_interval_millis';
    this.SYNC_VERSUS_SEARCHING_TIME = 'sync_versus_searching_millis';
    this.SYNC_MAX_WAIT_TIME = 'sync.maximumWaitTime';
    this.SYNC_NOTIF_BACKGROUND_TITLE = 'sync.pushNotifs.background.title';
    this.SYNC_NOTIF_OPPONENT_TITLE = 'sync.pushNotifs.opponentFound.title';
    this.SYNC_NOTIF_BACKGROUND_BODY = 'sync.pushNotifs.background.body';
    this.SYNC_NOTIF_OPPONENT_BODY = 'sync.pushNotifs.opponentFound.body';
    this.SYNC_VERSUS_HANDSHAKE = 'sync_versus_handshake_millis';
    this.SYNC_VERSUS_EST_TIME_MIN = 'sync.versus.estimatedTime.min';
    this.SYNC_VERSUS_EST_TIME_MAX = 'sync.versus.estimatedTime.max';
    this.SYNC_VERSUS_EST_TIME_MESSAGE = 'sync.versus.estimatedTime.message';
    this.SYNC_VERSUS_NOTIF_PROMPT_INTERVAL = 'sync.versus.pushNotifPromptInterval';
    this.SYNC_VS_OPPONENT_MODAL = 'sync.versus.opponentCancelled.modalDuration';
    this.SYNC_VS_BEGIN_COUNTDOWN = 'sync_versus_match_begin_countdown_seconds';
    this.SYNC_VS_TIES_BEGIN_COUNTDOWN = 'sync_ties_countdown_seconds';
    this.SYNC_VERSUS_SEARCHING_INTERVAL = 'sync_versus_searching_interval';
    this.SYNC_TIES_TRANSITION_SCREEN = 'sync_ties_transition_screen_duration_millis';
    this.SYNC_MATCHMAKING_ALLOW_SLEEP = 'sync.matchmaking.allow.sleep';

    // 1vN constants
    this.SYNC_1vN_ENABLED = 'sync.1vn.enabled';

    // FTUE constants
    this.SYNC_FTUE_GAMES_REQUIRED = 'sync.ftue.n.forced.games';
    this.SYNC_FTUE_MAX_WAIT_TIME = 'sync.ftue.max.wait.time';
    this.FTUE_TYPE = 'ftue.type';
    this.SYNC_FTUE_CHECK_MAX_WAIT_TIME_CLIENT = 'sync_ftue_check_max_wait_time_client';
    this.SYNC_FTUE_TRAINER_BOT_CARD_ENABLED = 'sync.ftue.trainerbot.card.enabled';
    this.FTUE_PROFILE_ORDER = 'ftue.avatargame';
    this.IS_SHOW_FTUE_CASH_MATCHES = 'ftue.firstmatchshowcash.enabled';
    this.IS_ASYNC_FTUE_BOT_ENABLED = 'ftue.trainingbot.enabled';
    // FTUE FLIT-MS1 Split Account Creation Flow constants
    this.FTUE_SPLIT_ACCOUNT_CREATION_ENABLED = 'ftue.split.account.creation.enabled';
    this.SKIP_HOME_FTUE = 'ftue.skiphome.enabled';
    this.FTUE_SKIP_MATCH_TUTORIAL = 'ftue.skip.match.tutorial';
    /// FTUE Slidehshow
    this.FTUE_SLIDESHOW_V2_ENABLED = 'ftue.slideshow.v2.enabled';
    this.FTUE_SLIDESHOW_SLIDE_AMOUNT = 'ftue.slideshow.v2.number';
    this.FTUE_SLIDESHOW_SLIDE1_BODY = 'ftue.slideshow.v2.one.body.copy';
    this.FTUE_SLIDESHOW_SLIDE1_BACKGROUND = 'ftue.slideshow.v2.one.asset';
    this.FTUE_SLIDESHOW_SLIDE2_BODY = 'ftue.slideshow.v2.two.body.copy';
    this.FTUE_SLIDESHOW_SLIDE2_HEADER = 'ftue.slideshow.v2.two.header.copy';
    this.FTUE_SLIDESHOW_SLIDE2_BACKGROUND = 'ftue.slideshow.v2.two.asset';
    this.FTUE_SLIDESHOW_SLIDE3_BODY = 'ftue.slideshow.v2.three.body.copy';
    this.FTUE_SLIDESHOW_SLIDE3_HEADER = 'ftue.slideshow.v2.three.header.copy';
    this.FTUE_SLIDESHOW_SLIDE3_BACKGROUND = 'ftue.slideshow.v2.three.asset';
    this.FTUE_SLIDESHOW_SLIDE4_BODY = 'ftue.slideshow.v2.four.body.copy';
    this.FTUE_SLIDESHOW_SLIDE4_HEADER = 'ftue.slideshow.v2.four.header.copy';
    this.FTUE_SLIDESHOW_SLIDE4_BACKGROUND = 'ftue.slideshow.v2.four.asset';
    this.FTUE_FEATURE_ENABLED = 'ftue_feature_enabled';
    this.FTUE_SCREEN_VISIBLE = 'ftue_screen_visible';
    this.FTUE_FALLBACK_PATH = 'ftue_fallback_path';
    this.FTUE_SCREEN_FALLBACK_MESSAGE = 'ftue_screen_fallback_message';
    this.FTUE_PATH_ORDER = 'ftue_path_order';
    this.FTUE_PREFERRED_SIGNUP_METHOD = 'ftue_preferred_signup_method';
    this.FTUE_USE_SKILLZ_THEME = 'ftue_use_skillz_theme';
    this.FTUE_V2_EMAIL_VERIFICATION_PRIZE_BONUS = 'ftue_v2.email_verification.prize.bonus';
    this.FTUE_V2_LOBBY_TOOLTIP_MENU = 'ftue_v2.lobby.tooltip.menu';
    this.FTUE_V2_LOBBY_TOOLTIP_STORE = 'ftue_v2.lobby.tooltip.store';
    this.FTUE_V2_LOBBY_TOOLTIP_ADD_CASH = 'ftue_v2.lobby.tooltip.add_cash';
    this.FTUE_V2_LOGIN_UI_ENABLED = 'ftue_v2.login.ui.enabled';
    this.FTUE_V2_RESULT_SCREEN_MESSAGE_FALLBACK = 'ftue_v2.result_screen.message.fallback';
    this.FTUE_V2_RESULT_SCREEN_MESSAGE_HIGH_PERCENTILE =
      'ftue_v2.result_screen.message.high_percentile';
    this.FTUE_V2_RESULT_SCREEN_MESSAGE_LOW_PERCENTILE =
      'ftue_v2.result_screen.message.low_percentile';
    this.FTUE_V2_RESULT_SCREEN_MESSAGE_MEDIUM_PERCENTILE_HIGH_END =
      'ftue_v2.result_screen.message.medium_percentile.high_end';
    this.FTUE_V2_RESULT_SCREEN_MESSAGE_MEDIUM_PERCENTILE_LOW_END =
      'ftue_v2.result_screen.message.medium_percentile.low_end';
    this.FTUE_V2_RESULT_SCREEN_OFFER_PREFERENCE = 'ftue_v2.result_screen.offer.preference';
    this.FTUE_V2_RESULT_SCREEN_PERCENTILE_LOW_THRESHOLD =
      'ftue_v2.result_screen.percentile.low.threshold';
    this.FTUE_V2_RESULT_SCREEN_PERCENTILE_MEDIUM_THRESHOLD =
      'ftue_v2.result_screen.percentile.medium.threshold';
    this.FTUE_V2_RESULT_SCREEN_PERCENTILE_MEDIUM_USER_COUNT_THRESHOLD =
      'ftue_v2.result_screen.percentile.medium.user_count.threshold';

    // Player Chat constants
    this.PLAYER_CHAT_ENTRYPOINT_VERSION = 'playerChat.entrypoint.version';
    this.PLAYER_CHAT_ENABLED = 'playerChat.enabled';
    this.PLAYER_CHAT_BAR_MESSAGE_DISPLAY = 'playerChat.chatBar.messageDisplayInterval';
    this.PLAYER_CHAT_BAR_REFRESH_INTERVAL = 'playerChat.chatBar.refreshInterval';
    this.PLAYER_CHAT_BAR_FETCHED_MESSAGES = 'playerChat.chatBar.numFetchedMessages';
    this.PLAYER_CHAT_BLOCKED_LIMIT = 'playerChat.blocked.limit';
    this.PLAYER_CHAT_MUTED_LIMIT = 'playerChat.muted.limit';
    this.PLAYER_CHAT_CONTACTS_LIMIT = 'playerChat.contacts.limit';
    this.PLAYER_CHAT_ROOM_CHAR_DISPLAY_LIMIT = 'playerChat.rooms.characterDisplayLimit';
    this.PLAYER_CHAT_PHONE_VERIFY_PROMPT_INTERVAL = 'playerChat.prompts.phoneVerify.promptInterval';
    this.PLAYER_CHAT_MESSAGE_RATE_LIMIT = 'playerChat.rateLimit.messageCount';
    this.PLAYER_CHAT_RATE_LIMIT_TIME_FRAME = 'playerChat.rateLimit.timeFrame';
    this.PLAYER_CHAT_RATE_LIMIT_MIN_DISPLAY = 'playerChat.rateLimit.minErrorDisplayTime';
    this.PLAYER_CHAT_MESSAGE_CHAR_LIMIT = 'playerChat.messages.characterLimit';
    this.PLAYER_CHAT_COPY_CONFIRM_DURATION = 'playerChat.messages.copyConfirmationDuration';
    this.PLAYER_CHAT_MUTED_DMS_SHOWN = 'playerChat.muted.numberDMsShown';
    this.PLAYER_CHAT_DM_MAX_PEOPLE = 'playerChat.DM.maxParticipants';
    this.PLAYER_CHAT_PHONE_VERIFY_IMAGE = 'playerChat.phoneVerify.image';
    this.PLAYER_CHAT_PHONE_VERIFY_HEADER_COPY = 'playerChat.prompts.phoneVerify.headerCopy';
    this.PLAYER_CHAT_PHONE_VERIFY_PROMPT_COPY = 'playerChat.prompts.phoneVerify.copy';
    this.PLAYER_CHAT_PHONE_VERIFY_NUMBER_INPUT = 'playerChat.phoneVerify.numberInput';
    this.PLAYER_CHAT_PHONE_VERIFY_NUMBER_CTA = 'playerChat.phoneVerify.numberCTA';
    this.PLAYER_CHAT_PHONE_VERIFY_CODE_INPUT = 'playerChat.phoneVerify.codeInput';
    this.PLAYER_CHAT_PHONE_VERIFY_CODE_CTA = 'playerChat.phoneVerify.codeCTA';
    this.PLAYER_CHAT_PROFILE_REQUEST_LIMIT = 'playerChat.contacts.profileRequestLimit';
    this.PLAYER_CHAT_CONTACTS_POLLING = 'playerChat.contacts.processingPollInterval';
    this.PLAYER_CHAT_CONTACTS_UPDATE_INTERVAL_1 = 'playerChat.contacts.updateInterval';
    this.PLAYER_CHAT_CONTACTS_UPDATE_INTERVAL_2 = 'playerChat.contacts.UpdateInterval';
    this.PLAYER_CHAT_CONTACTS_UPDATE_SIZE = 'playerChat.contacts.updateSize';
    this.PLAYER_CHAT_MESSAGES_LOADED_COUNT = 'playerChat.messages.loadedMessageCount';
    this.PLAYER_CHAT_ROOM_RECONNECT_TIME = 'playerChat.rooms.reconnectTime';
    this.PLAYER_CHAT_ROOM_RECONNECT_TOLERANCE = 'playerChat.rooms.reconnectTolerance';
    this.PLAYER_CHAT_ROOM_RECONNECT_MAX = 'playerChat.rooms.reconnectTimeMax';
    this.PLAYER_CHAT_APP_WIDE_UNREAD_INTERVAL = 'playerChat.unread.appWideInterval';
    this.PLAYER_CHAT_UNREAD_INTERVAL = 'playerChat.unread.chatInterval';
    this.PLAYER_CHAT_DO_NOT_PROMPT_DAYS = 'playerChat.notifications.doNotPromptDays';
    this.PLAYER_CHAT_FTUE_GAMES_REQUIRED = 'playerChat.FTUE.gamesRequired';
    this.PLAYER_CHAT_FTUE_SWIPE_GESTURES = 'playerChat.FTUE.swipeGestures';
    this.PLAYER_CHAT_FTUE_GROUP_CHAT_OPEN = 'playerChat.FTUE.groupChatOpen';
    this.PLAYER_CHAT_FTUE_ENABLED = 'playerChat.ftue.enabled';
    this.PLAYER_CHAT_SIDEBAR_V2_ENABLED = 'playerChat.sidebar.v2.enabled';
    this.PLAYER_CHAT_OPEN_SIDEBAR_FIRST = 'playerChat.open.sidebar.first';
    this.PLAYER_CHAT_FRIEND_SEARCH_LIMIT = 'playerChat.friends.search.limit';
    this.PLAYER_CHAT_CONTACTS_BATCH_SIZE = 'playerChat.contacts.batchSize'; // Limit for multi card vCard fetches

    // Moderation constants
    this.PLAYER_CHAT_TEMP_BAN = 'playerChat.moderation.tempBanHours';
    this.PLAYER_CHAT_TEMP_MUTE = 'playerChat.moderation.tempMuteHours';
    this.PLAYER_CHAT_MOD_COMMENT_MIN = 'playerChat.moderation.minCommentLength';
    this.PLAYER_CHAT_MOD_COMMENT_MAX = 'playerChat.moderation.maxCommentLength';

    // GDPR constants
    this.GDPR_OPT_IN_LANGUAGE = 'gdpr_optin_language';
    this.GDPR_ENABLED = 'gdpr_enabled';

    this.PLAYER_LEVELS_ENABLED = 'game_level_enabled';

    // Homescreen Constants
    this.HOMESCREEN_OPENING_SCREEN = 'opening.screen';
    this.HOME_STATUS_TICKETZ_NAVIGATION = 'home.status.ticketz.navigation';

    // Play Tab Constants
    this.MATCH_CODE_VISIBILITY_ENABLED = 'match.code.visibility.enabled';
    this.SKILLZ_PROMOTION_ENABLED = 'skillz.promotion.enabled';
    this.THREE_PLUS_H2H_STYLE_V2_ENABLED = 'three.plus.H2H.style.v2.enabled';
    this.GAME_OPERATIONS_CONTAINER_ENABLED = 'game.operations.container.enabled';
    this.GAME_OPERATIONS_LOCKED_EVENT_BUTTON_ENABLED =
      'game.operations.locked.event.button.enabled';
    this.GAME_OPERATIONS_TUTORIAL_ENABLED = 'game.operations.tutorial.trainer.enabled';
    this.GAME_OPERATIONS_TUTORIAL_IMAGE_URL = 'game.operations.tutorial.trainer.image.url';
    this.GAME_OPERATIONS_REFERRAL_IMAGE_URL = 'image.game.operations.container.referral.icon';

    // Friend Tournaments Constants
    this.FRIENDS_TOURNAMENTS_INCENTIVES_MAX = 'friends.tournaments.incentive.challenge.max';
    this.FRIENDS_TOURNAMENTS_PLAYER_ENABLED_FOR_USER = 'friends.tournaments.player.enabled'; // only the client property, user is allowed or not
    this.FRIENDS_TOURNAMENTS_PLAYER_ENABLED = 'friends_tournaments_player_enabled'; // combination of the client property + presence of friends tournament templates
    this.FRIENDS_TOURNAMENTS_NUDGE_MINUTES_REMAINING =
      'friends.tournaments.nudge_minutes_remaining';
    this.FRIENDS_TOURNAMENTS_NUDGE_MINUTES_COOL_DOWN =
      'friends.tournaments.nudge_minutes_cool_down';
    this.FRIENDS_TOURNAMENTS_CONTACT_WARNING_ENABLED =
      'friends.tournaments.contact.warning.enabled';
    this.FRIENDS_TOURNAMENTS_CONSENT_MESSAGE = 'friends.tournaments.consent.message';
    this.FRIENDS_TOURNAMENTS_INVITE_METHOD = 'friends.tournaments.invite.method';
    this.FRIENDS_TOURNAMENTS_INVITE_ENABLED = 'friends.tournaments.invite.enabled';
    this.FRIENDS_TOURNAMENTS_MEDALS_ENABLED = 'friends_tournament_medals_enabled';

    // Friends V2 constants
    this.FRIENDS_TOURNAMENTS_PHONE_NUMBER = 'friends.tournaments.phone.number';
    this.FRIENDS_TOURNAMENTS_OFFSYSTEM_FRIENDS_TILE_COPY =
      'friends.tournaments.offsystem.friends.tile.copy';
    this.FRIENDS_TOURNAMENTS_FREE_ENTRY_BANNER = 'friends.tournaments.free.entry.banner';
    this.FRIENDS_TOURNAMENTS_RECOMMENDED_MINUTES = 'friends.tournaments.recommended.minutes';
    this.FRIENDS_TOURNAMENTS_NUDGE_METHOD = 'friends.tournaments.nudge.method';
    this.FRIENDS_TOURNAMENTS_NUDGE_MINUTES_REMAINING_OFFSYSTEM =
      'friends.tournaments.nudge_minutes_remaining.offsystem';
    this.FRIENDS_TOURNAMENTS_NUDGE_MINUTES_COOL_DOWN_OFFSYSTEM =
      'friends.tournaments.nudge_minutes_cool_down.offsystem';
    this.FRIENDS_TOURNAMENTS_OFFSYSTEM_CRITERIA = 'friends.tournaments.offsystem.criteria';
    this.FRIENDS_TOURNAMENTS_OFF_SYSTEM_ACCOUNT_CREATION_MINUTES =
      'friends.tournaments.off.system.account.creation.minutes';
    this.FRIENDS_TOURNAMENTS_GAME_ACCOUNTS = 'friends.tournaments.game.accounts';
    this.FRIENDS_TOURNAMENTS_RATINGS_ENABLED = 'friends_tournaments_ratings_enabled';
    this.SAVE_ACCOUNT_BADGE_GAMES_REQUIRED = 'save.account.badge.games.required';
    this.SAVE_ACCOUNT_BADGE_MINUTES = 'save.account.badge.minutes';
    this.VERIFY_PHONE_BADGE_GAMES_REQUIRED = 'verify.phone.badge.games.required';
    this.VERIFY_PHONE_BADGE_MINUTES = 'verify.phone.badge.minutes';
    this.FRIENDS_TOURNAMENTS_OFFSYSTEM_INVITE_COPY = 'friends.tournaments.offsystem.invite.copy';
    this.FRIENDS_TOURNAMENTS_RECOMMENDED_INVITE_COPY =
      'friends.tournaments.recommended.invite.copy';

    // Rating Prompt Constants
    this.RATING_PROMPT_TICKETZ_THRESHOLD = 'ratingPrompt.ticketz.ticketzThreshold';
    this.RATING_PROMPT_Z_TROPHY_THRESHOLD = 'ratingPrompt.trophy.zThreshold';
    this.RATING_PROMPT_CASH_TROPHY_THRESHOLD = 'ratingPrompt.trophy.cashThreshold';
    this.RATING_PROMPT_TICKETZ_TROPHY_THRESHOLD = 'ratingPrompt.trophy.ticketzThreshold';
    this.RATING_PROMPT_TICKETZ_REWARD_IDS = 'ratingPrompt.ticketz.ticketzRewardIds';
    this.RATING_PROMPT_TROPHY_IDS = 'ratingPrompt.trophy.trophyIds';
    this.RATING_PROMPT_AFTER_WIN_SESSION_SECS = 'ratingPrompt.afterWin.sessionSeconds';
    this.RATING_PROMPT_AFTER_WIN_WIN_RATE = 'ratingPrompt.afterWin.winRate';
    this.RATING_PROMPT_AFTER_WIN_INSTALL_TIME = 'ratingPrompt.afterWin.installSeconds';
    this.RATING_PROMPT_INSTALL_SECONDS = 'ratingPrompt.general.installSeconds';
    this.RATING_PROMPT_REMIND_SECONDS = 'ratingPrompt.general.remindSeconds';
    this.RATING_PROMPT_EVENTS_REQUIRED = 'ratingPrompt.general.eventsRequired';
    this.RATING_PROMPT_SESSIONS_PER_PROMPT = 'ratingPrompt.general.sessionsPerPrompt ';
    this.RATING_PROMPT_WEEKLY_SESSIONS_PER_PROMPT = 'ratingPrompt.general.weeklySessionsPerPrompt';

    // Skillz News Constants
    this.SKILLZ_NEWS_ENABLED = 'news.enabled';
    this.SKILLZ_NEWS_BANNER_LOCATION = 'news.banner.location';
    this.SKILLZ_NEWS_BANNER_COUNT = 'news.banner.count';
    this.SKILLZ_NEWS_CHANNEL_NAME = 'news_channel_name';
    this.SKILLZ_NEWS_SCROLLABLE = 'news.scrollable';

    // Pooled Liquidity Constants
    this.POOLED_LIQUIDITY_TILE_COPY = 'pooled.liquidity.tile.copy';
    this.POOLED_LIQUIDITY_TILE_COPY_SELECTED = 'pooled.liquidity.tile.copy.selected';
    this.POOLED_LIQUIDITY_STATES_SELECTOR_COPY = 'pooled.liquidity.states.selector.copy';

    // Sync fastmatch Constants
    this.SYNC_FASTMATCH_HEADER_COPY = 'sync.fastmatch.header.copy';
    this.SYNC_FASTMATCH_SUBHEADER_COPY = 'sync.fastmatch.subheader.copy';

    // Fast Match v2
    this.SYNC_FASTMATCH_V2_ENABLED = 'fastmatch.v2.enabled';
    this.FAST_MATCH_TIPS_ENABLED = 'fastmatch.tips.and.tricks.enabled';
    this.FASTMATCH_V2_INFO_HEADER = 'fastmatch.v2.info.header.copy';
    this.FASTMATCH_V2_INFO_BODY = 'fastmatch.v2.info.body.copy';
    this.SYNC_FASTMATCH_ACCEPT_MATCH_COPY = 'fastmatch.v2.accept.match.copy';
    this.SYNC_FASTMATCH_STAKE_EMPTY_COPY = 'fastmatch.v2.bottom.drawer.empty.copy';
    this.FAST_MATCH_CONFIRMATION_TIME = 'fastmatch_confirmation_time';

    // Store Constants
    this.STORE_PLAYERS_HAVE_WON = 'store.players.have.won';
    this.STORE_DEPOSIT_COPY = 'store.deposit.copy';
    this.STORE_WITHDRAW = 'store.withdraw';
    this.LOGIN_BONUS_ENABLED = 'login.bonus.enabled';
    this.TICKETZ_STORE_CAROUSE_ENABLED = 'store.ticketz.entry.enabled';
    this.TROPHIES_BUTTON_ENABLED = 'store.trophy.entry.enabled';

    //Insufficient Funds Modal Constants
    this.OUT_OF_FUNDS_DEPOSIT_MODAL = 'outfunds.deposit.modal';

    // Low Z Balance LTO Constants
    this.LOW_Z_BALANCE_LTO_ENABLED = 'low.z.balance.lto.enabled';
    this.LOW_Z_BALANCE_LTO_ID = 'low.z.balance.lto.id';

    // Trophy Constants
    this.TROPHIES_PAGE_VERSION = 'trophies.page.version';

    // Account Menu Constants
    this.PUSH_NOTIFICATIONS_SETTINGS_ENABLED = 'push.notifications.settings.enabled';
    this.ACCOUNT_DELETION_ENABLED = 'account.deletion.enabled';
    this.ACCOUNT_DELETION_MODAL_COPY = 'account.deletion.modal.copy';

    // Checkout Constants
    this.CHECKOUT_DETAILS_SHOW_BONUS = 'checkout.show.bonus';
    this.CHECKOUT_DETAILS_SHOW_NEW_BALANCE = 'checkout.show.new.balance';
    this.CHECKOUT_BILLING_ADDRESS_REQUIRED = 'checkout.billingAddress.required';
    this.CHECKOUT_DEFAULT_APPLEPAY = 'checkout.default.ApplePay';
    this.CHECKOUT_ENABLE_CREDITCARD_SCAN = 'checkout.enable.creditCard.scan';
    this.CHECKOUT_V2_PAYPAL_USE_BRAINTREE = 'checkout.v2.paypal.use.braintree';
    this.CHECKOUT_VENMO_ENABLED = 'checkout.venmo.enabled';
    this.CHECKOUT_VENMO_BANNER = 'checkout.venmo.banner';
    this.CHECKOUT_VENMO_MODAL = 'checkout.venmo.modal';
    this.CHECKOUT_VENMO_ENABLED_HIGHLIGHTED = 'checkout.venmo.enabled.highlighted';
    this.CHECKOUT_ACH_DEPOSIT_ENABLED = 'checkout.ach.deposit.enabled';
    this.CHECKOUT_ALLOW_DEPOSIT_DURING_WITHDRAWAL = 'checkout.allow.deposit.during.withdrawal';
    this.CHECKOUT_ACH_SECURITY_MESSAGING_ENABLED = 'checkout.ach.security.messaging.enabled';

    // LTO Constants
    this.ADDFUNDS_LTO_MODAL = 'addFunds.LTO.modal';
    this.LTO_ON_PLAY = 'LTO.on.play';
    this.LTO_ON_STORE = 'LTO.on.store';

    // PCI
    this.PCI_EPROTECT_ENABLED = 'pci.eprotect.on';
    this.PCI_TRANSACT_WITHOUT_THREATMETRIX = 'pci.transact.without.threatmetrix';

    // 3rd party ad constants
    this.THIRD_PARTY_ADS_ENABLED = '3rdparty.ads.enabled';
    this.THIRD_PARTY_ADS_ENABLED_WITH_INTERSTITIALS = '3rd.party.ads.enabled.with.interstitials';
    this.THIRD_PARTY_ADS_DOUBLE_WINNING_RECOVER_LOSS_MODAL_RESET =
      '3rdparty.ads.double.winning.recover.loss.modal.reset';
    this.THIRD_PARTY_ADS_WITH_REWARD = '3rdparty.ads.with.reward';
    this.THIRD_PARTY_ADS_WITHOUT_REWARD = '3rdparty.ads.without.reward';
    this.THIRD_PARTY_ADS_HOUSE_ADS_PERCENTAGE = '3rdparty.ads.house.ads.percentage';
    this.STORE_CLAIMZ_ENABLED = 'store.claimz.enabled';

    // Game Constants
    this.FTUE_MATCH_CODE_FOCUSED = 'ftue_match_code_focused';
    this.FTUE_MATCH_CODE_FRIENDS = 'ftue_match_code_friends';

    // Tier Badges Constants
    this.PROGRESSION_MODAL_ENABLED = 'progression.modal.enabled';

    // Loyalty Store Constants
    this.LOYALTY_ENABLED = 'loyalty.store.enabled';
    this.SKILLZ_REWARDS_CELL_ENABLED = 'loyalty.store.cell.enabled';

    // Ticketz Constants
    this.TICKETZ_TIER_USE_SCALABLE_IMAGE = 'tier.icon.use.scalable.image';
    this.TICKETZ_TIER_BADGES_ENABLED = 'tier.badge.enabled';

    // IDFA Constants
    this.IDFA_FTUE_PROMPT_SETTNG = 'idfa.ftue.prompt';
    this.IDFA_DEPOSIT_PROMPT_ENABLED = 'idfa.deposit.prompt';
    this.IDFA_FIRST_OPEN_PLAY = 'idfa.first.open.play';

    // Hosted Tournaments
    this.LIVE_EVENT_HOSTED_ENABLED = 'live.event.hosted.enabled';

    // Play Tab Improvement (auto expand of Head to Head Section)
    this.AUTO_EXPAND_PRO_H2H_AFTER_X_MATCHES = 'auto.expand.pro.h2h.templates.after.x.matches';

    // Live Tournament Templates Auto-Expansion
    this.AUTO_EXPAND_LIVE_TOURNAMENT_TEMPLATES_AFTER_X_MATCHES =
      'auto.expand.livetournament.templates.after.x.matches';

    // Progression Constants
    this.DEV_PROGRESSION_HOME_ENABLED = 'progression.skillz.dev.progression.home.enabled';
    this.DEV_PROGRESSION_SIDEBAR_ENABLED = 'progression.skillz.dev.progression.sidebar.enabled';

    // Tutorial Constants
    this.FTUE_WEB_TUTORIAL_SCREEN_ENABLED = 'ftue_web_tutorial_enabled';

    //Currency Constants
    this.FTUE_CURRENCY_PROMPT_ENABLED = 'ftue.currency.prompt.enabled';

    // Welcome Back 3.0
    this.WELCOME_BACK_V3_ENABLED = 'welcome.back.v3.enabled';

    //Leagues
    this.LEAGUES_VERSION = 'leagues.version'; //just in case we need to revert to old league code
    this.LEAGUES_TOAST_ENABLED = 'leagues.position.toast.enabled';
    this.LEAGUES_NEGATIVE_TOAST_ENABLED = 'leagues.negative.position.toast.enabled';
    this.LEAGUES_COMBINED_MEDALS_ENABLED = 'leagues.combinedMedals.enabled';
    this.LEAGUES_LOCKED_FTUE_V2_ENABLED = 'leagues.lockedFTUEv2.enabled';
    this.LEAGUES_V3_MEDAL_LEADERBOARD_ENABLED = 'leagues.v3.medal.leaderboard.enabled'; //game property

    // X-Button Experiment
    this.RESULT_NEXT_BUTTON_ENABLED = 'results.next.button.enabled';

    //SMS Verification
    this.VERIFY_SMS_SAVEACCOUNT = 'verify.sms.saveaccount';
    this.VERIFY_SMS_GAMEPLAY = 'verify.sms.gameplay';
    this.VERIFY_SMS_DEPOSIT = 'verify.sms.deposit';
    this.VERIFY_SMS_CLOSE_BUTTON = 'verify.sms.exit';

    // Federated Login and Sign Up Constants
    this.FEDLOGIN_GOOGLE_IOS_LOGIN = 'fedLogin.google.ios.login';
    this.FEDLOGIN_GOOGLE_ANDROID_LOGIN = 'fedLogin.google.android.login';
    this.FEDLOGIN_APPLE_IOS_LOGIN = 'fedLogin.apple.ios.login';
    this.FEDLOGIN_APPLE_ANDROID_LOGIN = 'fedLogin.apple.android.login';
    this.FEDLOGIN_FACEBOOK_IOS_LOGIN = 'fedLogin.facebook.ios.login';
    this.FEDLOGIN_FACEBOOK_ANDROID_LOGIN = 'fedLogin.facebook.android.login';
    this.FEDLOGIN_GOOGLE_IOS_SIGNUP = 'fedLogin.google.ios.signup';
    this.FEDLOGIN_GOOGLE_ANDROID_SIGNUP = 'fedLogin.google.android.signup';
    this.FEDLOGIN_APPLE_IOS_SIGNUP = 'fedLogin.apple.ios.signup';
    this.FEDLOGIN_APPLE_ANDROID_SIGNUP = 'fedLogin.apple.android.signup';
    this.FEDLOGIN_FACEBOOK_IOS_SIGNUP = 'fedLogin.facebook.ios.signup';
    this.FEDLOGIN_FACEBOOK_ANDROID_SIGNUP = 'fedLogin.facebook.android.signup';

    // Enable/Disable SMS/Email Sign-In
    this.SSO_LOGIN_SMS_ENABLED = 'sso.login.SMS.enabled';
    this.SSO_LOGIN_EMAIL_ENABLED = 'sso.login.email.enabled';

    // Default Sign-In Constant
    this.SSO_LOGIN_EMAIL_DEFAULT = 'sso.login.email.default';

    // Core Loop Tungsten AB Testing
    this.CORELOOP_TUNGSTEN_ENABLED = 'coreloop.tungsten.enabled';
    this.CORELOOP_TUNGSTEN_LITE_ENABLED = 'coreloop.tungsten.lite.enabled';

    // RUM
    this.RUM_ENABLED = 'monitoring.sdk.enabled';
    this.IS_ERROR_HANDLER_ENABLED = 'error.handler.enabled';
    this.IS_ERROR_BOUNDARY_ENABLED = 'error.boundary.enabled';
    this.RUM_SESSION_SAMPLE_RATE = 'monitoring.sdk.session.sample.rate';
    this.RUM_NATIVE_CRASH_DETECTION = 'monitoring.sdk.native.crash.detection.enabled';
    this.RUM_RESOURCE_SAMPLE_RATE = 'monitoring.sdk.resource.trace.sample.rate';
    //Spectate
    this.SPECTATE_ENABLED_RESULTS = 'spectate.enabled.results';

    // Cash Game Registration
    this.SKIP_CASH_GAME_FULL_REG_CHECK = 'skip_cash_game_full_reg_check';

    // FTUE
    this.AVATAR_RANDOMIZER_ENABLED = 'ftue.avatar.randomizer.enabled';
    this.AVATAR_RANDOMIZER_ONLY_FTUE = 'ftue.avatar.randomizer.only.ftue';
    this.USERNAME_RANDOMIZER_ENABLED = 'ftue.username.randomizer.enabled';
    this.FTUE_REVAMP_ENABLED = 'ftue.revamp.ui.enabled';
    this.COMPRESSED_BIRTHDAY_OPT_IN = 'ftue.compressed.birthday.opt.in';
    this.MATCH_TEMPLATE_FTUE_ENABLED = 'match.template.ftue.enabled';
    this.TUTORIAL_TRAINERS_IMAGE_SET = 'tutorial.trainers.image.set';

    // FTUE Conversions (Free Cash Match)
    this.FTUE_CONVERSIONS_ENABLED = 'ftue.conversions.enabled';

    this.EVENT_CHAT_ENABLED = 'live.event.chat.enabled';
    this.LIVE_BRACKET_EVENT_CHAT_ENABLED = 'live.event.brackets.chat.enabled';
    this.SHOW_MORE_TEXT_BUTTONS_ENABLED = 'show.more.play.tab.text.enabled';
    this.LIVE_BRACKET_PREVENT_JOIN_SECS = 'live.bracket.prevent.join.secs';
    // Interstitials
    this.INTERSTITIALS_ENABLED = 'interstitials.enabled';
    this.INTERSTITIALS_HOUSEADS_ENABLED = 'interstitials.houseads.enabled';
    this.INTERSTITIALS_GLOBAL_COOLDOWN = 'interstitials.global.cooldown.seconds';
    this.DAILY_BONUS_CASH_SCREEN_COOLDOWN = 'daily.bonus.cash.screen.cooldown.seconds';
    this.DAILY_BONUS_CASH_SCREEN_MAX_REDIRECT_COUNT = 'daily.bonus.cash.screen.max.redirect.count';
    this.TOURNAMENT_TIMER_LENGTH_SECONDS = 'tournament_timer_length_seconds';
  }

  // ============================================================================
  // FTUE DEVELOPMENT OVERRIDES - Remove this entire section when not needed
  // ============================================================================
  /**
   * Enable this flag to override FTUE feature flags for local development
   * Set to true to enable overrides, false to use server values
   */
  private ENABLE_FTUE_DEV_OVERRIDES = false;

  /**
   * Development overrides for FTUE feature flags
   * Modify these values to test different FTUE configurations locally
   * @returns Override values if ENABLE_FTUE_DEV_OVERRIDES is true, null otherwise
   */
  private getFTUEDevOverrides(): FTUEDevOverrides | null {
    if (!this.ENABLE_FTUE_DEV_OVERRIDES) {
      return null;
    }

    // Configure your override values here
    return {
      ftueFeatureEnabled: false,
      ftueScreenVisible: true,
      ftueFallbackPath: 'BEGINNER',
      ftueScreenFallbackMessage: 'How well do you know Skillz?',
      ftuePathOrder: ['BEGINNER', 'PRO'],
      ftuePreferredSignupMethod: 'email',
      ftueUseSkillzTheme: false,
      ftueV2EmailVerificationPrizeBonus: '5.00',
      ftueV2LobbyTooltipMenu: false,
      ftueV2LobbyTooltipStore: false,
      ftueV2LobbyTooltipAddCash: false,
      ftueV2LoginUIEnabled: true,
      ftueV2ResultScreenMessageFallback: 'Try a cash game! Your best score could be one run away.',
      ftueV2ResultScreenMessageHighPercentile:
        "You're among the best! Challenge tougher tournaments.",
      ftueV2ResultScreenMessageLowPercentile:
        'Better luck next time! Keep playing more to be the next Champion!',
      ftueV2ResultScreenMessageMediumPercentileHighEnd:
        'Awesome! You have scored more than {0} first time players',
      ftueV2ResultScreenMessageMediumPercentileLowEnd:
        'Sweet start! Your breakthrough is one play away.',
      ftueV2ResultScreenOfferPreference: 'LTO',
      ftueV2ResultScreenPercentileLowThreshold: 30,
      ftueV2ResultScreenPercentileMediumThreshold: 70,
      ftueV2ResultScreenPercentileMediumUserCountThreshold: 5000,
    };
  }
  // ============================================================================
  // END FTUE DEVELOPMENT OVERRIDES
  // ============================================================================

  // Interstitial
  getInterstitialsEnabled(): boolean {
    return Preferences.getBoolClientProperty(this.INTERSTITIALS_ENABLED, false);
  }

  getInterstitialsHouseAdsEnabled(): boolean {
    return Preferences.getBoolClientProperty(this.INTERSTITIALS_HOUSEADS_ENABLED, false);
  }

  getInterstitialsGlobalCooldown(): number {
    return Preferences.getIntClientProperty(this.INTERSTITIALS_GLOBAL_COOLDOWN, 60);
  }

  getDailyBonusCashScreenCooldown(): number {
    // Default 4 hours (4 * 60 * 60 = 14400 seconds)
    return Preferences.getIntClientProperty(this.DAILY_BONUS_CASH_SCREEN_COOLDOWN, 14400);
  }

  getDailyBonusCashScreenMaxRedirectCount(): number {
    return Preferences.getIntClientProperty(this.DAILY_BONUS_CASH_SCREEN_MAX_REDIRECT_COUNT, 5); // Default 5 times
  }
  // Assorted constants

  getVersusCountdownTime(): number {
    return Preferences.getIntClientProperty(this.VS_COUNTDOWN_TIME);
  }

  getVersusSkipTime(): number {
    return Preferences.getIntClientProperty(this.VS_SKIP_TIME);
  }

  getVersusZPlayAgainMax(): number {
    return Preferences.getIntClientProperty(this.VS_Z_PLAY_AGAIN_MAX);
  }

  getVersusCashPlayAgainMax(): number {
    return Preferences.getIntClientProperty(this.VS_CASH_PLAY_AGAIN_MAX);
  }

  getVersusSyncPlayAgainMax(): number {
    return Preferences.getIntClientProperty(this.VS_SYNC_PLAY_AGAIN_MAX, 100000);
  }

  getVersusSyncZPlayAgainMax(): number {
    return Preferences.getIntClientProperty(this.VS_SYNC_PLAY_AGAIN_MAX_Z, 10000);
  }

  getLocalNotifAbortTime(): number {
    return Preferences.getIntClientProperty(this.LOCAL_NOTIF_ABORT_TIME);
  }

  getPushPromptConfig(): string {
    const config = Preferences.getClientProperty(this.PUSH_PROMPT_CONFIG);
    return (
      Object.values(PushPromptConfig).find((configValue: string): boolean => {
        return config === configValue;
      }) || PushPromptConfig.Default
    );
  }

  getPushDoublePromptEnabled(): boolean {
    return Preferences.getBoolClientProperty(PUSH_DOUBLE_PROMPT_ENABLED, false);
  }

  /**
   * Returns whether "Uh Oh!" dialog suppression is enabled.
   * When enabled, transient network errors (401, 408, 502, 503, 504) and
   * network connectivity issues won't show "Uh Oh!" dialogs to users.
   * Default: false (show dialogs as before)
   */
  getSuppressUhOhDialog(): boolean {
    return Preferences.getBoolClientProperty(SUPPRESS_UHOH_DIALOG, false);
  }

  getPushSecondsBetweenPrompt(): number {
    return Preferences.getIntClientProperty(PUSH_SECONDS_BETWEEN_PROMPT, 60 * 60 * 24); // default 1 day
  }

  getLegalUrl(): string {
    return Preferences.getClientProperty(this.LEGAL_URL);
  }

  getRewardCalendarEnabled(): boolean {
    return Preferences.getBoolClientProperty(this.REWARD_CALENDAR_ENABLED, false);
  }

  getRewardSystemsEnabled(): boolean {
    return Preferences.getBoolClientProperty(REWARD_SYSTEMS_ENABLED, false);
  }

  getRewardSystemsV2Enabled(): boolean {
    return Preferences.getBoolClientProperty(REWARD_SYSTEMS_V2_ENABLED, false);
  }

  getRewardSystemsModalTriggerIntervalSeconds(): number {
    const threeHoursInSeconds = 10800;
    return Preferences.getIntClientProperty(
      REWARD_SYSTEMS_MODAL_TRIGGER_INTERVAL_SECONDS,
      threeHoursInSeconds
    );
  }

  getRewardSystemsCountdownToAssignSeconds(): number {
    const defaultTimeInSeconds = 60;
    return Preferences.getIntClientProperty(
      REWARD_SYSTEMS_COUNTDOWN_TO_ASSIGN,
      defaultTimeInSeconds
    );
  }

  // Referral

  getReferralV2Enabled(): boolean {
    return Preferences.getBoolClientProperty(this.REFERRAL_V2_ENABLED, false);
  }

  getReferralSideMenuButtonCopy(): string {
    return (
      Preferences.getClientProperty(this.REFERRAL_SIDE_MENU_BUTTON_COPY) ??
      I18n.message('Free Bonus Cash')
    );
  }

  getReferralStoreButtonEnabled(): boolean {
    return Preferences.getBoolClientProperty(this.REFERRAL_STORE_BUTTON_ENABLED, false);
  }

  getReferralStoreButtonCopy(): string {
    // NOTE: Referrals are disabled for the INR MVP, so this is not a concern before the 2nd phase of International work
    return (
      Preferences.getClientProperty(this.REFERRAL_STORE_BUTTON_COPY) ??
      I18n.message('GET $20 BONUS CASH\nINVITE FRIENDS')
    );
  }

  getReferralNewUiWithEarnings(): boolean {
    return Preferences.getBoolClientProperty(this.REFERRAL_NEW_UI_WITH_EARNINGS, false);
  }

  getReferralNewUiWagerPercentage(): string {
    return (
      Preferences.getClientProperty(this.REFERRAL_NEW_UI_WAGERING_PERCENTAGE) ?? I18n.message('10%')
    );
  }

  getReferralNewUiTotalBonusCash(): number {
    return Preferences.getIntClientProperty(this.REFERRAL_NEW_UI_TOTAL_BONUS_CASH, 1000);
  }

  getReferralNewUiShareText(): string {
    return (
      Preferences.getClientProperty(this.REFERRAL_NEW_UI_SHARE_TEXT) ??
      I18n.message(
        "Join me in competitive mobile gaming on Skillz! Use my promo code {0} and you'll earn {1} on your first deposit in ANY Skillz game. Plus, I'd earn {2} on your deposit and 10% of your wagers up to $30. Check out the games here:"
      )
    );
  }

  getReferralNewUiStoreCashThreshold(): number {
    return Preferences.getIntClientProperty(this.REFERRAL_NEW_UI_STORE_CASH_THRESHOLD, 5.0);
  }

  // In App Notifications

  getInAppNotificationDisplayInterval(): number {
    return Preferences.getIntClientProperty(this.IN_APP_NOTIF_DISPLAY_INTERVAL, 2000);
  }

  getInAppNotificationDisplayLength(): number {
    return Preferences.getIntClientProperty(this.IN_APP_NOTIF_DISPLAY_LENGTH, 5000);
  }

  // Welcome Back constants

  getWBDefaultToOpen(): boolean {
    return Preferences.getBoolClientProperty(this.WB_DEFAULT_TO_OPEN, false);
  }

  getWBAnimationEnabled(): boolean {
    return Preferences.getBoolClientProperty(this.WB_ANIMATION_ENABLED, true);
  }

  getWBCellsOpenAllVibrationCount(): number {
    return Preferences.getIntClientProperty(this.WB_CELL_OPEN_ALL_VIBRATION, 2);
  }

  getWBAnimationSpeed(): number {
    return Preferences.getIntClientProperty(this.WB_ANIMATION_SPEED, 2000);
  }

  getWBCellsOpenOneVibrationCount(): number {
    return Preferences.getIntClientProperty(this.WB_CELL_OPEN_ONE_VIBRATION, 1);
  }

  getWBFlowEnabled(): boolean {
    return Preferences.getBoolClientProperty(this.WB_FLOW_ENABLED, true);
  }

  getWBDailyChallengesEnabled(): boolean {
    return Preferences.getBoolClientProperty(this.WB_DAILY_CHALLENGES_ENABLED, true);
  }

  // Live Activity Constants
  getLiveActivityEnabled(): boolean {
    return Preferences.getBoolClientProperty(LIVE_ACTIVITY_ENABLED, true);
  }

  getLiveActivityMaxDayLimit(): number {
    return Preferences.getIntClientProperty(LIVE_ACTIVITY_MAX_DAY_LIMIT, 20);
  }

  // Live Event FTUE Constants
  getLiveEventFTUETimeframeSeconds(): number {
    return Preferences.getIntClientProperty(this.LIVE_EVENT_FTUE_TIMEFRAME_SECONDS, 480);
  }

  // Live Event constants

  getLiveEventJoinCutOff(): number {
    return Preferences.getIntClientProperty(this.LIVE_EVENT_JOIN_CUTOFF);
  }

  getLiveEventCreationEnabled(): boolean {
    return Preferences.getBoolClientProperty(this.LIVE_EVENT_CREATION_ENABLED, false);
  }

  getLiveBracketsSkipStreamFlow(): boolean {
    return Preferences.getBoolClientProperty(LIVE_BRACKETS_SKIP_STREAM_FLOW, true);
  }

  getLiveBracketsShowStripInBracketDetails(): boolean {
    return Preferences.getBoolClientProperty(LIVE_BRACKETS_SHOW_STRIP_IN_BRACKET_DETAILS, false);
  }

  getLiveBracketsShowStripInLobby(): boolean {
    return Preferences.getBoolClientProperty(LIVE_BRACKETS_SHOW_STRIP_IN_LOBBY, false);
  }

  getLiveBracketsShowToastBeforeStartSeconds(): number {
    return Preferences.getIntClientProperty(LIVE_BRACKETS_SHOW_TOAST_BEFORE_START_SECONDS, 180);
  }

  getLiveBracketsV1Enabled(): boolean {
    return Preferences.getBoolClientProperty(LIVE_BRACKETS_V1_ENABLED, false);
  }

  // Limited Use Tournament constants
  getLimitedUseTournamentJoinCutOff(): number {
    return Preferences.getIntClientProperty(LIMITED_USE_JOIN_CUTOFF, 420);
  }

  getLimitedUseTournamentPlayAgainEnabled(): boolean {
    return Preferences.getBoolClientProperty(LIMITED_USE_TOURNAMENT_PLAY_AGAIN_ENABLED, true);
  }

  // Virtual Currency constants

  getIsVirtualCurrencyOnly(): boolean {
    return Preferences.getBoolClientProperty(this.VIRTUAL_CURRENCY_ONLY);
  }

  // Sync Play constants

  getSyncTiebreakerMaxTimeAllowed(): number {
    return Preferences.getIntClientProperty(this.SYNC_TIEBREAKER_MAX_TIME, 5000);
  }

  getSyncOpponentFoundNumVibrations(): number {
    return Preferences.getIntClientProperty(this.SYNC_OPPONENT_NUM_VIBRATIONS, 2);
  }

  getSyncLiveMatchConfirmation(): boolean {
    return Preferences.getBoolClientProperty(this.SYNC_LIVE_MATCH_CONFIRMATION, false);
  }

  getIsSyncCellEnabled(): boolean {
    return Preferences.getBoolClientProperty(this.SYNC_CELL_ENABLED, false);
  }

  getSyncMinPingStrength(): number {
    return Preferences.getIntClientProperty(this.SYNC_MIN_PING_STRENGTH, 3000);
  }

  getSyncStrongPingStrength(): number {
    return Preferences.getIntClientProperty(this.SYNC_STRONG_PING_STRENGTH, 500);
  }

  getSyncPingAverageCount(): number {
    return Preferences.getIntClientProperty(this.SYNC_PING_AVERAGE_COUNT, 5);
  }

  getSyncPingCheckInterval(): number {
    return Preferences.getIntClientProperty(
      this.SYNC_PING_CHECK_INTERVAL,
      SKZConstants.MS_PER_HALF_SEC
    );
  }

  getSyncVersusSearchingTime(): number {
    return Preferences.getIntClientProperty(this.SYNC_VERSUS_SEARCHING_TIME, 500);
  }

  getSyncMaxWaitTime(): number {
    return Preferences.getIntClientProperty(this.SYNC_MAX_WAIT_TIME, 400000);
  }

  getSyncNotifBackgroundTitle(): string {
    return Preferences.getClientProperty(this.SYNC_NOTIF_BACKGROUND_TITLE) || _m('Searching');
  }

  getSyncNotifOpponentFoundTitle(): string {
    return Preferences.getClientProperty(this.SYNC_NOTIF_OPPONENT_TITLE) || _m('Match Found!');
  }

  getSyncNotifBackgroundBody(): string {
    return (
      Preferences.getClientProperty(this.SYNC_NOTIF_BACKGROUND_BODY) ||
      'We will notify you when we find an opponent in {{{match_name}}}'
    );
  }

  getSyncNotifOpponentFoundBody(): string {
    return (
      Preferences.getClientProperty(this.SYNC_NOTIF_OPPONENT_BODY) ||
      'Found opponent for {{{match_name}}}. Join in {{{handshake_length}}} seconds.'
    );
  }

  getSyncVersusHandshake(): number {
    return Preferences.getIntClientProperty(this.SYNC_VERSUS_HANDSHAKE, 15000);
  }

  getSyncVersusEstimatedTimeMin(): number {
    return Preferences.getIntClientProperty(this.SYNC_VERSUS_EST_TIME_MIN, 1);
  }

  getSyncVersusEstimatedTimeMax(): number {
    return Preferences.getIntClientProperty(this.SYNC_VERSUS_EST_TIME_MAX, 5);
  }

  getSyncVersusEstimatedTimeMessage(): string {
    return Preferences.getClientProperty(this.SYNC_VERSUS_EST_TIME_MESSAGE);
  }

  getSyncVersusNotifPromptInterval(): number {
    return Preferences.getIntClientProperty(this.SYNC_VERSUS_NOTIF_PROMPT_INTERVAL, 5);
  }

  getSyncVersusOpponentModalDuration(): number {
    return Preferences.getIntClientProperty(this.SYNC_VS_OPPONENT_MODAL, 3000);
  }

  getSyncVersusBeginCountdown(): number {
    return Preferences.getIntClientProperty(this.SYNC_VS_BEGIN_COUNTDOWN, 3);
  }

  getSyncVersusTiesBeginCountdown(): number {
    return Preferences.getIntClientProperty(this.SYNC_VS_TIES_BEGIN_COUNTDOWN, 5);
  }

  getSyncVersusSearchingInterval(): number {
    return Preferences.getIntClientProperty(
      this.SYNC_VERSUS_SEARCHING_INTERVAL,
      SKZConstants.MS_PER_HALF_SEC
    );
  }

  getSyncTiesTransitionScreenDuration(): number {
    return Preferences.getIntClientProperty(this.SYNC_TIES_TRANSITION_SCREEN, 2000);
  }

  getSyncMatchMakingAllowSleep(): boolean {
    return Preferences.getBoolClientProperty(this.SYNC_MATCHMAKING_ALLOW_SLEEP, false);
  }

  // 1vN constants
  getSync1vNEnabled(): boolean {
    return Preferences.getBoolClientProperty(this.SYNC_1vN_ENABLED, false);
  }

  // FTUE constants

  getNumberSyncFTUEGames(): number {
    return Preferences.getIntClientProperty(this.SYNC_FTUE_GAMES_REQUIRED, 2);
  }

  getSyncFTUEMaxWaitTime(): number {
    return Preferences.getIntClientProperty(this.SYNC_FTUE_MAX_WAIT_TIME, 30000);
  }

  getFTUEType(): string {
    return Preferences.getClientProperty(this.FTUE_TYPE) || 'Async';
  }

  getSyncFTUECheckMaxWaitTime(): boolean {
    return Preferences.getBoolClientProperty(this.SYNC_FTUE_CHECK_MAX_WAIT_TIME_CLIENT, false);
  }

  getSyncFTUETrainerBotCardEnabled(): boolean {
    return Preferences.getBoolClientProperty(this.SYNC_FTUE_TRAINER_BOT_CARD_ENABLED, false);
  }

  getProfilePositionInFTUE(): string {
    const config = Preferences.getClientProperty(this.FTUE_PROFILE_ORDER);
    return (
      Object.values(FTUECreateProfilePosition).find((configValue: string): boolean => {
        return config === configValue;
      }) || FTUECreateProfilePosition.FirstOpen
    );
  }

  getMatchTemplateFTUEEnabled() {
    return (
      this.getFTUEConversionsEnabled() || // Also enable match template FTUE for conversions flow
      Preferences.getBoolClientProperty(this.MATCH_TEMPLATE_FTUE_ENABLED, false)
    );
  }

  // FTUE FLIT-MS1 Split Account Creation Flow constants
  getFTUESplitAccountCreationEnabled(): boolean {
    return Preferences.getBoolClientProperty(this.FTUE_SPLIT_ACCOUNT_CREATION_ENABLED, false);
  }

  isSkipHomeScreenFtue(): boolean {
    return Preferences.getBoolClientProperty(this.SKIP_HOME_FTUE, false);
  }

  getFTUESkipMatchTutorial(): boolean {
    return Preferences.getBoolClientProperty(this.FTUE_SKIP_MATCH_TUTORIAL, false);
  }

  // FTUE Slideshow Constants

  getFTUESlideshowV2Enabled(): boolean {
    return Preferences.getBoolClientProperty(this.FTUE_SLIDESHOW_V2_ENABLED, false);
  }

  getFTUESlideshowNumSlides(): number {
    // yes, the client property value is a string representing the number of slides.
    const options = ['ZERO', 'ONE', 'TWO', 'THREE', 'FOUR'];
    const defaultValue = 3; // chosen by product

    const config = Preferences.getClientProperty(this.FTUE_SLIDESHOW_SLIDE_AMOUNT);
    const number = options.indexOf(config?.toUpperCase());

    // check that number was found and valid and if not, return default
    return number >= 0 ? number : defaultValue;
  }

  getFTUESlideshowSlide1Body(): string {
    return Preferences.getClientProperty(this.FTUE_SLIDESHOW_SLIDE1_BODY);
  }

  getFTUESlideshowSlide1Background(): string {
    return Preferences.getClientProperty(this.FTUE_SLIDESHOW_SLIDE1_BACKGROUND);
  }

  getFTUESlideshowSlide2Body(): string {
    return Preferences.getClientProperty(this.FTUE_SLIDESHOW_SLIDE2_BODY);
  }

  getFTUESlideshowSlide2Header(): string {
    return Preferences.getClientProperty(this.FTUE_SLIDESHOW_SLIDE2_HEADER);
  }

  getFTUESlideshowSlide2Background(): string {
    return Preferences.getClientProperty(this.FTUE_SLIDESHOW_SLIDE2_BACKGROUND);
  }

  getFTUESlideshowSlide3Body(): string {
    return Preferences.getClientProperty(this.FTUE_SLIDESHOW_SLIDE3_BODY);
  }

  getFTUESlideshowSlide3Header(): string {
    return Preferences.getClientProperty(this.FTUE_SLIDESHOW_SLIDE3_HEADER);
  }

  getFTUESlideshowSlide3Background(): string {
    return Preferences.getClientProperty(this.FTUE_SLIDESHOW_SLIDE3_BACKGROUND);
  }

  getFTUESlideshowSlide4Body(): string {
    return Preferences.getClientProperty(this.FTUE_SLIDESHOW_SLIDE4_BODY);
  }

  getFTUESlideshowSlide4Header(): string {
    return Preferences.getClientProperty(this.FTUE_SLIDESHOW_SLIDE4_HEADER);
  }

  getFTUESlideshowSlide4Background(): string {
    return Preferences.getClientProperty(this.FTUE_SLIDESHOW_SLIDE4_BACKGROUND);
  }

  getFTUEFeatureEnabled(): boolean {
    const overrides = this.getFTUEDevOverrides();
    if (overrides) return overrides.ftueFeatureEnabled;

    if (Platform.OS === 'web') {
      return false;
    }
    return Preferences.getBoolClientProperty(this.FTUE_FEATURE_ENABLED, false);
  }

  getFTUEScreenVisible(): boolean {
    const overrides = this.getFTUEDevOverrides();
    if (overrides) return overrides.ftueScreenVisible;

    if (Platform.OS === 'web') {
      return false;
    }
    return Preferences.getBoolClientProperty(this.FTUE_SCREEN_VISIBLE, true);
  }

  getFTUEFallbackPath(): string {
    const overrides = this.getFTUEDevOverrides();
    if (overrides) return overrides.ftueFallbackPath;

    return Preferences.getClientProperty(this.FTUE_FALLBACK_PATH) || 'BEGINNER';
  }

  getFTUEScreenFallbackMessage(): string | null {
    const overrides = this.getFTUEDevOverrides();
    if (overrides) return overrides.ftueScreenFallbackMessage;

    return Preferences.getClientProperty(this.FTUE_SCREEN_FALLBACK_MESSAGE);
  }

  getFTUEPathOrder(): string[] {
    const overrides = this.getFTUEDevOverrides();
    if (overrides) return overrides.ftuePathOrder;

    const pathOrder = Preferences.getClientProperty(this.FTUE_PATH_ORDER);
    if (pathOrder) {
      try {
        return JSON.parse(pathOrder);
      } catch (e) {
        return ['BEGINNER', 'PRO'];
      }
    }
    return ['BEGINNER', 'PRO'];
  }

  getFTUEPreferredSignupMethod(): string {
    const overrides = this.getFTUEDevOverrides();
    if (overrides) return overrides.ftuePreferredSignupMethod;

    return Preferences.getClientProperty(this.FTUE_PREFERRED_SIGNUP_METHOD) || 'email';
  }

  getFTUEUseSkillzTheme(): boolean {
    const overrides = this.getFTUEDevOverrides();
    if (overrides) return overrides.ftueUseSkillzTheme;

    if (Platform.OS === 'web') {
      return false;
    }
    return Preferences.getBoolClientProperty(this.FTUE_USE_SKILLZ_THEME, false);
  }

  getFTUEV2EmailVerificationPrizeBonus(): string {
    const overrides = this.getFTUEDevOverrides();
    if (overrides) return overrides.ftueV2EmailVerificationPrizeBonus;

    return Preferences.getClientProperty(this.FTUE_V2_EMAIL_VERIFICATION_PRIZE_BONUS) || '0.00';
  }

  getFTUEV2LobbyTooltipMenu(): boolean {
    const overrides = this.getFTUEDevOverrides();
    if (overrides) return overrides.ftueV2LobbyTooltipMenu;

    if (Platform.OS === 'web') {
      return false;
    }
    return Preferences.getBoolClientProperty(this.FTUE_V2_LOBBY_TOOLTIP_MENU, false);
  }

  getFTUEV2LobbyTooltipStore(): boolean {
    const overrides = this.getFTUEDevOverrides();
    if (overrides) return overrides.ftueV2LobbyTooltipStore;

    if (Platform.OS === 'web') {
      return false;
    }
    return Preferences.getBoolClientProperty(this.FTUE_V2_LOBBY_TOOLTIP_STORE, false);
  }

  getFTUEV2LobbyTooltipAddCash(): boolean {
    const overrides = this.getFTUEDevOverrides();
    if (overrides) return overrides.ftueV2LobbyTooltipAddCash;

    if (Platform.OS === 'web') {
      return false;
    }
    return Preferences.getBoolClientProperty(this.FTUE_V2_LOBBY_TOOLTIP_ADD_CASH, false);
  }

  getFTUEV2LoginUIEnabled(): boolean {
    const overrides = this.getFTUEDevOverrides();
    if (overrides) return overrides.ftueV2LoginUIEnabled;

    if (Platform.OS === 'web') {
      return false;
    }
    return Preferences.getBoolClientProperty(this.FTUE_V2_LOGIN_UI_ENABLED, false);
  }

  getFTUEV2FeatureEnabled(): boolean {
    return this.getFTUEFeatureEnabled() && this.getFTUEV2LoginUIEnabled();
  }

  getFTUEV2ResultScreenMessageFallback(): string {
    const overrides = this.getFTUEDevOverrides();
    if (overrides) return overrides.ftueV2ResultScreenMessageFallback;

    return (
      Preferences.getClientProperty(this.FTUE_V2_RESULT_SCREEN_MESSAGE_FALLBACK) ||
      'Try a cash game! Your best score could be one run away.'
    );
  }

  getFTUEV2ResultScreenMessageHighPercentile(): string {
    const overrides = this.getFTUEDevOverrides();
    if (overrides) return overrides.ftueV2ResultScreenMessageHighPercentile;

    return (
      Preferences.getClientProperty(this.FTUE_V2_RESULT_SCREEN_MESSAGE_HIGH_PERCENTILE) ||
      "You're among the best! Challenge tougher tournaments."
    );
  }

  getFTUEV2ResultScreenMessageLowPercentile(): string {
    const overrides = this.getFTUEDevOverrides();
    if (overrides) return overrides.ftueV2ResultScreenMessageLowPercentile;

    return (
      Preferences.getClientProperty(this.FTUE_V2_RESULT_SCREEN_MESSAGE_LOW_PERCENTILE) ||
      'Better luck next time! Keep playing more to be the next Champion!'
    );
  }

  getFTUEV2ResultScreenMessageMediumPercentileHighEnd(): string {
    const overrides = this.getFTUEDevOverrides();
    if (overrides) return overrides.ftueV2ResultScreenMessageMediumPercentileHighEnd;

    return (
      Preferences.getClientProperty(
        this.FTUE_V2_RESULT_SCREEN_MESSAGE_MEDIUM_PERCENTILE_HIGH_END
      ) || 'Awesome! You have scored more than ${0} first time players'
    );
  }

  getFTUEV2ResultScreenMessageMediumPercentileLowEnd(): string {
    const overrides = this.getFTUEDevOverrides();
    if (overrides) return overrides.ftueV2ResultScreenMessageMediumPercentileLowEnd;

    return (
      Preferences.getClientProperty(this.FTUE_V2_RESULT_SCREEN_MESSAGE_MEDIUM_PERCENTILE_LOW_END) ||
      'Sweet start! Your breakthrough is one play away.'
    );
  }

  getFTUEV2ResultScreenOfferPreference(): string {
    const overrides = this.getFTUEDevOverrides();
    if (overrides) return overrides.ftueV2ResultScreenOfferPreference;

    return Preferences.getClientProperty(this.FTUE_V2_RESULT_SCREEN_OFFER_PREFERENCE) || 'LTO';
  }

  getFTUEV2ResultScreenPercentileLowThreshold(): number {
    const overrides = this.getFTUEDevOverrides();
    if (overrides) return overrides.ftueV2ResultScreenPercentileLowThreshold;

    return Preferences.getIntClientProperty(
      this.FTUE_V2_RESULT_SCREEN_PERCENTILE_LOW_THRESHOLD,
      30
    );
  }

  getFTUEV2ResultScreenPercentileMediumThreshold(): number {
    const overrides = this.getFTUEDevOverrides();
    if (overrides) return overrides.ftueV2ResultScreenPercentileMediumThreshold;

    return Preferences.getIntClientProperty(
      this.FTUE_V2_RESULT_SCREEN_PERCENTILE_MEDIUM_THRESHOLD,
      70
    );
  }

  getFTUEV2ResultScreenPercentileMediumUserCountThreshold(): number {
    const overrides = this.getFTUEDevOverrides();
    if (overrides) return overrides.ftueV2ResultScreenPercentileMediumUserCountThreshold;

    return Preferences.getIntClientProperty(
      this.FTUE_V2_RESULT_SCREEN_PERCENTILE_MEDIUM_USER_COUNT_THRESHOLD,
      5000
    );
  }

  // Player Chat constants

  getPlayerChatEntryPointVersion(): string {
    return Preferences.getClientProperty(this.PLAYER_CHAT_ENTRYPOINT_VERSION);
  }

  getIsPlayerChatEnabled(): boolean {
    return Preferences.getBoolClientProperty(this.PLAYER_CHAT_ENABLED);
  }

  getPlayerChatBarMessageDisplayInterval(): number {
    return Preferences.getIntClientProperty(
      this.PLAYER_CHAT_BAR_MESSAGE_DISPLAY,
      SKZConstants.MS_PER_SEC
    );
  }

  getPlayerChatBarRefreshInterval(): number {
    return Preferences.getIntClientProperty(this.PLAYER_CHAT_BAR_REFRESH_INTERVAL, 10000);
  }

  getPlayerChatBarFetchedMessages(): number {
    return Preferences.getIntClientProperty(this.PLAYER_CHAT_BAR_FETCHED_MESSAGES, 5);
  }

  getPlayerChatBlockedLimit(): number {
    return Preferences.getIntClientProperty(this.PLAYER_CHAT_BLOCKED_LIMIT, 3000);
  }

  getPlayerChatMutedLimit(): number {
    return Preferences.getIntClientProperty(this.PLAYER_CHAT_MUTED_LIMIT, 250);
  }

  getPlayerChatContactsLimit(): number {
    return Preferences.getIntClientProperty(this.PLAYER_CHAT_CONTACTS_LIMIT, 3000);
  }

  getPlayerChatRoomCharDisplayLimit(): number {
    return Preferences.getIntClientProperty(this.PLAYER_CHAT_ROOM_CHAR_DISPLAY_LIMIT, 25);
  }

  getPlayerChatPhoneVerifyPromptInterval(): number {
    return Preferences.getIntClientProperty(this.PLAYER_CHAT_PHONE_VERIFY_PROMPT_INTERVAL, 20);
  }

  getPlayerChatMessageRateLimit(): number {
    return Preferences.getIntClientProperty(this.PLAYER_CHAT_MESSAGE_RATE_LIMIT, 6);
  }

  getPlayerChatRateLimitTimeFrame(): number {
    return Preferences.getIntClientProperty(this.PLAYER_CHAT_RATE_LIMIT_TIME_FRAME, 10);
  }

  getPlayerChatRateLimitMinErrorDisplay(): number {
    return Preferences.getIntClientProperty(this.PLAYER_CHAT_RATE_LIMIT_MIN_DISPLAY, 1000);
  }

  getPlayerChatMessageCharacterLimit(): number {
    return Preferences.getIntClientProperty(this.PLAYER_CHAT_MESSAGE_CHAR_LIMIT, 250);
  }

  getPlayerChatCopyConfirmDuration(): number {
    return Preferences.getIntClientProperty(this.PLAYER_CHAT_COPY_CONFIRM_DURATION, 2000);
  }

  getPlayerChatMutedDMsShown(): number {
    return Preferences.getIntClientProperty(this.PLAYER_CHAT_MUTED_DMS_SHOWN, 5);
  }

  getPlayerChatDMMaxPeople(): number {
    return Preferences.getIntClientProperty(this.PLAYER_CHAT_DM_MAX_PEOPLE, 10);
  }

  getPlayerChatPhoneVerifyImage(): string {
    return Preferences.getClientProperty(this.PLAYER_CHAT_PHONE_VERIFY_IMAGE);
  }

  getPlayerChatPhoneVerifyHeaderCopy(): string {
    return Preferences.getClientProperty(this.PLAYER_CHAT_PHONE_VERIFY_HEADER_COPY);
  }

  getPlayerChatPhoneVerifyPromptCopy(): string {
    return Preferences.getClientProperty(this.PLAYER_CHAT_PHONE_VERIFY_PROMPT_COPY);
  }

  getPlayerChatPhoneVerifyNumberInput(): string {
    return Preferences.getClientProperty(this.PLAYER_CHAT_PHONE_VERIFY_NUMBER_INPUT);
  }

  getPlayerChatPhoneVerifyNumberCTA(): string {
    return Preferences.getClientProperty(this.PLAYER_CHAT_PHONE_VERIFY_NUMBER_CTA);
  }

  getPlayerChatPhoneVerifyCodeInput(): string {
    return Preferences.getClientProperty(this.PLAYER_CHAT_PHONE_VERIFY_CODE_INPUT);
  }

  getPlayerChatPhoneVerifyCodeCTA(): string {
    return Preferences.getClientProperty(this.PLAYER_CHAT_PHONE_VERIFY_CODE_CTA);
  }

  getPlayerChatProfileRequestLimit(): number {
    return Preferences.getIntClientProperty(this.PLAYER_CHAT_PROFILE_REQUEST_LIMIT, 3000);
  }

  getPlayerChatContactsPollingTime(): number {
    return Preferences.getIntClientProperty(
      this.PLAYER_CHAT_CONTACTS_POLLING,
      SKZConstants.MS_PER_HALF_SEC
    );
  }

  getPlayerChatContactsUpdateInterval(): number {
    return (
      Preferences.getIntClientProperty(this.PLAYER_CHAT_CONTACTS_UPDATE_INTERVAL_1) ||
      Preferences.getIntClientProperty(this.PLAYER_CHAT_CONTACTS_UPDATE_INTERVAL_2) ||
      30
    );
  }

  getPlayerChatContactsUpdateSize(): number {
    return Preferences.getIntClientProperty(this.PLAYER_CHAT_CONTACTS_UPDATE_SIZE, 200);
  }

  getPlayerChatMessagesLoadedCount(): number {
    return Preferences.getIntClientProperty(this.PLAYER_CHAT_MESSAGES_LOADED_COUNT, 50);
  }

  getPlayerChatRoomReconnectTime(): number {
    return Preferences.getIntClientProperty(this.PLAYER_CHAT_ROOM_RECONNECT_TIME, 3000);
  }

  getPlayerChatRoomReconnectTolerance(): number {
    return Preferences.getIntClientProperty(this.PLAYER_CHAT_ROOM_RECONNECT_TOLERANCE, 200);
  }

  getPlayerChatRoomReconnectTimeMax(): number {
    return Preferences.getIntClientProperty(this.PLAYER_CHAT_ROOM_RECONNECT_MAX, 30000);
  }

  getPlayerChatAppWideUnreadInterval(): number {
    return Preferences.getIntClientProperty(this.PLAYER_CHAT_APP_WIDE_UNREAD_INTERVAL, 10000);
  }

  getPlayerChatUnreadInterval(): number {
    return Preferences.getIntClientProperty(this.PLAYER_CHAT_UNREAD_INTERVAL, 2000);
  }

  getPlayerChatDoNotPromptDays(): number {
    return Preferences.getIntClientProperty(this.PLAYER_CHAT_DO_NOT_PROMPT_DAYS, 5);
  }

  getPlayerChatFriendSearchLimit(): number {
    return Preferences.getIntClientProperty(this.PLAYER_CHAT_FRIEND_SEARCH_LIMIT, 25);
  }

  getPlayerChatOpenSidebarFirst(): boolean {
    return Preferences.getBoolClientProperty(this.PLAYER_CHAT_OPEN_SIDEBAR_FIRST, false);
  }

  getPlayerChatContactsBatchSize(): number {
    return Preferences.getIntClientProperty(this.PLAYER_CHAT_CONTACTS_BATCH_SIZE, 10);
  }

  // Moderation Constants

  getPlayerChatTempBanHours(): number {
    return Preferences.getIntClientProperty(this.PLAYER_CHAT_TEMP_BAN, 24);
  }

  getPlayerChatTempMuteHours(): number {
    return Preferences.getIntClientProperty(this.PLAYER_CHAT_TEMP_MUTE, 24);
  }

  getPlayerChatModCommentMin(): number {
    return Preferences.getIntClientProperty(this.PLAYER_CHAT_MOD_COMMENT_MIN, 50);
  }

  getPlayerChatModCommentMax(): number {
    return Preferences.getIntClientProperty(this.PLAYER_CHAT_MOD_COMMENT_MAX, 1000);
  }

  // GDPR

  getGDPROptInLanguage(): string {
    return (
      Preferences.getClientProperty(this.GDPR_OPT_IN_LANGUAGE) ||
      _m('I consent to being contacted about Skillz products and offers.')
    );
  }

  getIsGDPREnabled(): boolean {
    return Preferences.getBoolClientProperty(this.GDPR_ENABLED, false);
  }

  getIsPlayerLevelsEnabled(): boolean {
    return Preferences.getBoolClientProperty(this.PLAYER_LEVELS_ENABLED, false);
  }

  // Homescreen tab changes

  getHomeStatusTicketzNavigation(): String {
    return (
      Preferences.getClientProperty(this.HOME_STATUS_TICKETZ_NAVIGATION) ||
      SKZConstants.TICKETZ_STORE
    );
  }

  // Play Tab Constants

  getMatchCodeVisibilityEnabled(): boolean {
    if (Platform.OS == 'web') {
      return true;
    } else {
      return Preferences.getBoolClientProperty(this.MATCH_CODE_VISIBILITY_ENABLED, false);
    }
  }

  getSkillzPromotionEnabled(): boolean {
    return Preferences.getBoolClientProperty(this.SKILLZ_PROMOTION_ENABLED, true);
  }

  getThreePlusH2HStyleV2Enabled(): boolean {
    return Preferences.getBoolClientProperty(this.THREE_PLUS_H2H_STYLE_V2_ENABLED, false);
  }

  getGameOperationsContainerEnabled(): boolean {
    // GameOperationsContainer relies on a working Interstitials system, so tie them together
    return (
      Preferences.getBoolClientProperty(this.GAME_OPERATIONS_CONTAINER_ENABLED, false) &&
      Preferences.getBoolClientProperty(this.INTERSTITIALS_ENABLED, false)
    );
  }

  getGameOperationsLockedEventButtonEnabled(): boolean {
    return Preferences.getBoolClientProperty(
      this.GAME_OPERATIONS_LOCKED_EVENT_BUTTON_ENABLED,
      false
    );
  }

  getGameOperationsTutorialEnabled(): boolean {
    return (
      this.getGameOperationsContainerEnabled() &&
      Preferences.getBoolClientProperty(this.GAME_OPERATIONS_TUTORIAL_ENABLED, false)
    );
  }

  getGameOperationsTutorialImageURL(): string {
    return Preferences.getClientProperty(this.GAME_OPERATIONS_TUTORIAL_IMAGE_URL);
  }

  getGameOperationsReferralImageURL(): string {
    return Preferences.getClientProperty(this.GAME_OPERATIONS_REFERRAL_IMAGE_URL);
  }

  // Friends Tournaments

  getFriendsTournamentIncentiveMax(): number {
    return Preferences.getIntClientProperty(this.FRIENDS_TOURNAMENTS_INCENTIVES_MAX, 3);
  }

  getFriendsTournamentEnabledForUser(): boolean {
    return Preferences.getBoolClientProperty(
      this.FRIENDS_TOURNAMENTS_PLAYER_ENABLED_FOR_USER,
      true
    );
  }

  getFriendsTournamentEnabled(): boolean {
    return Preferences.getBoolClientProperty(this.FRIENDS_TOURNAMENTS_PLAYER_ENABLED, false);
  }

  getFriendsTournamentNudgeMinutesRemaining(): number {
    return Preferences.getIntClientProperty(this.FRIENDS_TOURNAMENTS_NUDGE_MINUTES_REMAINING, 720);
  }

  getFriendsTournamentNudgeMinutesCoolDown(): number {
    return Preferences.getIntClientProperty(this.FRIENDS_TOURNAMENTS_NUDGE_MINUTES_COOL_DOWN, 360);
  }

  getFriendsTournamentContactWarningEnabled(): boolean {
    return Preferences.getBoolClientProperty(
      this.FRIENDS_TOURNAMENTS_CONTACT_WARNING_ENABLED,
      false
    );
  }

  getFriendsTournamentConsentMessage(): string {
    return (
      Preferences.getClientProperty(this.FRIENDS_TOURNAMENTS_CONSENT_MESSAGE) ||
      _m(
        'Looks like you have no one to play with yet, add your contacts to invite them via text and email.'
      )
    );
  }

  getFriendsTournamentInviteMethod(): string {
    return Preferences.getClientProperty(this.FRIENDS_TOURNAMENTS_INVITE_METHOD) || 'SERVER';
  }

  getFriendsTournamentInviteEnabled(): boolean {
    return Preferences.getBoolClientProperty(this.FRIENDS_TOURNAMENTS_INVITE_ENABLED, true);
  }

  getFriendsTournamentMedalsEnabled(): boolean {
    return Preferences.getBoolClientProperty(this.FRIENDS_TOURNAMENTS_MEDALS_ENABLED, false);
  }

  // Friends V2 getters
  getFriendsTournamentsPhoneNumber(): boolean {
    return Preferences.getBoolClientProperty(this.FRIENDS_TOURNAMENTS_PHONE_NUMBER, true);
  }

  getFriendsTournamentsOffsystemFriendsTileCopy(): string {
    return (
      Preferences.getClientProperty(this.FRIENDS_TOURNAMENTS_OFFSYSTEM_FRIENDS_TILE_COPY) ||
      _m('Invite New Friends!')
    );
  }

  getFriendsTournamentsFreeEntryBanner(): number {
    return Preferences.getIntClientProperty(this.FRIENDS_TOURNAMENTS_FREE_ENTRY_BANNER, 1);
  }

  getFriendsTournamentsRecommendedMinutes(): number {
    return Preferences.getIntClientProperty(this.FRIENDS_TOURNAMENTS_RECOMMENDED_MINUTES, 4320);
  }

  getFriendsTournamentsNudgeMethod(): String {
    return Preferences.getClientProperty(this.FRIENDS_TOURNAMENTS_NUDGE_METHOD);
  }

  getFriendsTournamentsNudgeMinutesRemainingOffsystem(): number {
    return Preferences.getIntClientProperty(
      this.FRIENDS_TOURNAMENTS_NUDGE_MINUTES_REMAINING_OFFSYSTEM,
      8640
    );
  }

  getFriendsTournamentsNudgeMinutesCoolDownOffsystem(): number {
    return Preferences.getIntClientProperty(
      this.FRIENDS_TOURNAMENTS_NUDGE_MINUTES_COOL_DOWN_OFFSYSTEM,
      1440
    );
  }

  getFriendsTournamentsOffsystemCriteria(): String {
    return Preferences.getClientProperty(this.FRIENDS_TOURNAMENTS_OFFSYSTEM_CRITERIA);
  }

  getFriendsTournamentsOffSystemAccountCreationMinutes(): number {
    return Preferences.getIntClientProperty(
      this.FRIENDS_TOURNAMENTS_OFF_SYSTEM_ACCOUNT_CREATION_MINUTES,
      10080
    );
  }

  getFriendsTournamentsGameAccounts(): number {
    return Preferences.getIntClientProperty(this.FRIENDS_TOURNAMENTS_GAME_ACCOUNTS, 3);
  }

  getFriendsTournamentsRatingsEnabled(): boolean {
    return Preferences.getBoolClientProperty(this.FRIENDS_TOURNAMENTS_RATINGS_ENABLED, false);
  }

  getRatingPromptTicketzThreshold(): number {
    return Preferences.getIntClientProperty(this.RATING_PROMPT_TICKETZ_THRESHOLD, 0);
  }

  getRatingPromptZTrophyThreshold(): number {
    return Preferences.getIntClientProperty(this.RATING_PROMPT_Z_TROPHY_THRESHOLD, -1);
  }

  getRatingPromptCashTrophyThreshold(): number {
    return Preferences.getIntClientProperty(this.RATING_PROMPT_CASH_TROPHY_THRESHOLD, 0);
  }

  getRatingPromptTicketzTrophyThreshold(): number {
    return Preferences.getIntClientProperty(this.RATING_PROMPT_TICKETZ_TROPHY_THRESHOLD, 100);
  }

  getRatingPromptTicketzRewardIds(): Array<String> {
    const ids = Preferences.getClientProperty(this.RATING_PROMPT_TICKETZ_REWARD_IDS);
    const arrayIds = ids ? ids.split(',') : null;
    return arrayIds;
  }

  getRatingPromptTrophyIds(): Array<String> {
    const ids = Preferences.getClientProperty(this.RATING_PROMPT_TROPHY_IDS);
    const arrayIds = ids ? ids.split(',') : null;
    return arrayIds;
  }

  getRatingPromptAfterWinSessionSeconds(): number {
    return Preferences.getIntClientProperty(this.RATING_PROMPT_AFTER_WIN_SESSION_SECS, 600);
  }

  getRatingPromptAfterWinWinRate(): number {
    return Preferences.getIntClientProperty(this.RATING_PROMPT_AFTER_WIN_WIN_RATE, 1);
  }

  getRatingPromptAfterWinInstallTime(): number {
    return Preferences.getIntClientProperty(this.RATING_PROMPT_AFTER_WIN_INSTALL_TIME, 604800);
  }

  getRatingPromptInstallSeconds(): number {
    return Preferences.getIntClientProperty(this.RATING_PROMPT_INSTALL_SECONDS, 172800);
  }

  getRatingPromptRemindSeconds(): number {
    return Preferences.getIntClientProperty(this.RATING_PROMPT_REMIND_SECONDS, 172800);
  }

  getRatingPromptEventsRequired(): number {
    return Preferences.getIntClientProperty(this.RATING_PROMPT_EVENTS_REQUIRED, 1);
  }

  getRatingPromptSessionsPerPrompt(): number {
    return Preferences.getIntClientProperty(this.RATING_PROMPT_SESSIONS_PER_PROMPT, 1);
  }

  getRatingPromptWeeklySessionsPerPrompt(): number {
    return Preferences.getIntClientProperty(this.RATING_PROMPT_WEEKLY_SESSIONS_PER_PROMPT, 1);
  }

  getSaveAccountBadgeGamesRequired(): number {
    return Preferences.getIntClientProperty(this.SAVE_ACCOUNT_BADGE_GAMES_REQUIRED, 10);
  }

  getSaveAccountBadgeMinutes(): number {
    return Preferences.getIntClientProperty(this.SAVE_ACCOUNT_BADGE_MINUTES, 20160);
  }

  getVerifyPhoneBadgeGamesRequired(): number {
    return Preferences.getIntClientProperty(this.VERIFY_PHONE_BADGE_GAMES_REQUIRED, 25);
  }

  getVerifyPhoneBadgeMinutes(): number {
    return Preferences.getIntClientProperty(this.VERIFY_PHONE_BADGE_MINUTES, 43200);
  }

  getFriendsTournamentsOffsystemInviteCopy(): String {
    return Preferences.getClientProperty(this.FRIENDS_TOURNAMENTS_OFFSYSTEM_INVITE_COPY);
  }

  getFriendsTournamentsRecommendedInviteCopy(): String {
    return Preferences.getClientProperty(this.FRIENDS_TOURNAMENTS_RECOMMENDED_INVITE_COPY);
  }

  // Game
  getFTUEMatchCodeFocused(): Boolean {
    return Preferences.getBoolClientProperty(this.FTUE_MATCH_CODE_FOCUSED, false);
  }

  getFTUEMatchCodeFriends(): Boolean {
    return Preferences.getBoolClientProperty(this.FTUE_MATCH_CODE_FRIENDS, false);
  }
  // Withdrawal

  getPlayerWithdrawalMethod(): string {
    return Preferences.getClientProperty(PLAYER_WITHDRAWAL_METHOD) || SKZConstants.PAYPAL;
  }

  getPlayerWithdrawalMaxAmount(): number {
    return Preferences.getIntClientProperty(PLAYER_WITHDRAWAL_PAYPAL_MAX_AMOUNT, 250);
  }

  getPlayerWithdrawalCancellationAllEnabled(): boolean {
    return Preferences.getBoolClientProperty(PLAYER_WITHDRAWAL_CANCELLATION_ALL_ENABLED, false);
  }

  getPlayerWithdrawalPayPalEnabled(): boolean {
    return Preferences.getBoolClientProperty(PLAYER_WITHDRAWAL_PAYPAL_ENABLED, true);
  }

  getPlayerWithdrawalAeropayEnabled(): boolean {
    return Preferences.getBoolClientProperty(PLAYER_WITHDRAWAL_AEROPAY_ENABLED, false);
  }

  getPlayerWithdrawalAeropayRTPEnabled(): boolean {
    return Preferences.getBoolClientProperty(PLAYER_WITHDRAWAL_AEROPAY_RTP_ENABLED, false);
  }

  getPlayerWithdrawalSideMenuButtonsEnabled(): boolean {
    return Preferences.getBoolClientProperty(PLAYER_WITHDRAWAL_SIDE_MENU_BUTTONS_ENABLED, false);
  }

  getPlayerWithdrawalAeropayBankPopupEnabled(): boolean {
    return (
      Preferences.getBoolClientProperty(PLAYER_WITHDRAWAL_AEROPAY_ENABLED, false) &&
      Preferences.getBoolClientProperty(PLAYER_WITHDRAWAL_AEROPAY_LINK_BANK_POPUP_ENABLED, false)
    );
  }

  getPlayerWithdrawalAeropayBankPopupIntervalMinutes(): number {
    const twoDaysInMinutes = MINUTES_PER_DAY * 2;
    return Preferences.getIntClientProperty(
      PLAYER_WITHDRAWAL_AEROPAY_BANK_POPUP_INTERVAL_MINUTES,
      twoDaysInMinutes
    );
  }

  getPlayerWithdrawalAeropayRTPFeeEnabled(): boolean {
    return Preferences.getBoolClientProperty(PLAYER_WITHDRAWAL_AEROPAY_RTP_FEE_ENABLED, true);
  }

  getPlaythroughEnabled(): boolean {
    return Preferences.getBoolClientProperty(PLAYTHROUGH_ENABLED, false);
  }

  getPlaythroughMultiplier(): number {
    return Preferences.getIntClientProperty(PLAYTHROUGH_MULTIPLIER, 1);
  }

  // Skillz News

  getSkillzNewsEnabled(): boolean {
    return Preferences.getBoolClientProperty(this.SKILLZ_NEWS_ENABLED, true);
  }

  getSkillzNewsBannerLocation(): string {
    return Preferences.getClientProperty(this.SKILLZ_NEWS_BANNER_LOCATION) || 'HOME';
  }

  getSkillzNewsBannerCount(): number {
    return Preferences.getIntClientProperty(this.SKILLZ_NEWS_BANNER_COUNT, 3);
  }

  getSkillzNewsChannelName(): string {
    return (
      Preferences.getClientProperty(this.SKILLZ_NEWS_CHANNEL_NAME) ||
      SKZConstants.DEFAULT_SKILLZ_NEWS_CHANNEL_NAME
    );
  }

  getSkillzNewsScrollable(): boolean {
    return Preferences.getBoolClientProperty(this.SKILLZ_NEWS_SCROLLABLE, false);
  }

  // Horizontal banners
  isHorizontalBannersEnabled(): boolean {
    return Preferences.getBoolClientProperty(HORIZONTAL_BANNERS_ENABLED, false);
  }

  // Pooled Liquidity

  getPooledLiquidityTileCopy(): string {
    return (
      Preferences.getClientProperty(this.POOLED_LIQUIDITY_TILE_COPY) || _m('Choose Prizes & Entry')
    );
  }

  getPooledLiquidityTileCopySelected(): string {
    return (
      Preferences.getClientProperty(this.POOLED_LIQUIDITY_TILE_COPY_SELECTED) ||
      _m('Selected Stakes')
    );
  }

  getPooledLiquidityStatesSelectorCopy(): string {
    return (
      Preferences.getClientProperty(this.POOLED_LIQUIDITY_STATES_SELECTOR_COPY) ||
      _m('Tap to select your stakes')
    );
  }

  // Sync FastMatch
  getSyncFastmatchHeaderCopy(): string {
    return (
      Preferences.getClientProperty(this.SYNC_FASTMATCH_HEADER_COPY) ||
      _m('Tap to select your stakes')
    );
  }

  getSyncFastmatchSubheaderCopy(): string {
    return (
      Preferences.getClientProperty(this.SYNC_FASTMATCH_SUBHEADER_COPY) ||
      _m('The more you select, the faster you will find an opponent')
    );
  }

  getSyncFastMatchAcceptMatchCopy(): string {
    return (
      Preferences.getClientProperty(this.SYNC_FASTMATCH_ACCEPT_MATCH_COPY) ||
      _m('Once the match has started, leaving will forfeit your entry fee')
    );
  }

  getFastMatchV2Enabled(): boolean {
    return Preferences.getBoolClientProperty(this.SYNC_FASTMATCH_V2_ENABLED, false);
  }

  getFastMatchStakesEmptyCopy(): string {
    return (
      Preferences.getClientProperty(this.SYNC_FASTMATCH_STAKE_EMPTY_COPY) ||
      _m('No stakes selected. The more you choose, the faster you match.')
    );
  }

  getFastMatchSearchingTipsEnabled(): boolean {
    return Preferences.getBoolClientProperty(this.FAST_MATCH_TIPS_ENABLED, false);
  }

  getFastMatchV2InfoHeader(): string {
    return Preferences.getClientProperty(this.FASTMATCH_V2_INFO_HEADER) || _m('Select Your Stakes');
  }

  getFastMatchV2InfoBody(): string {
    return (
      Preferences.getClientProperty(this.FASTMATCH_V2_INFO_BODY) ||
      _m(
        'Tap to select your stakes and we’ll match you with an opponent of similar skill. The more you select, the faster you match!'
      )
    );
  }

  // Store

  getStorePlayersHaveWon(): boolean {
    return Preferences.getBoolClientProperty(this.STORE_PLAYERS_HAVE_WON, true);
  }

  getStoreWithdraw(): boolean {
    return Preferences.getBoolClientProperty(this.STORE_WITHDRAW, false);
  }

  getStoreDepositCopy(): string {
    return (
      Preferences.getClientProperty(this.STORE_DEPOSIT_COPY) || _m('CHOOSE YOUR DEPOSIT AMOUNT:')
    );
  }

  getLoginBonusEnabled(): boolean {
    return Preferences.getBoolClientProperty(this.LOGIN_BONUS_ENABLED, false);
  }

  getTicketStoreCarouseEnabled(): boolean {
    return Preferences.getBoolClientProperty(this.TICKETZ_STORE_CAROUSE_ENABLED, false);
  }

  getStoreTrophiesButtonEnabled(): boolean {
    return Preferences.getBoolClientProperty(this.TROPHIES_BUTTON_ENABLED, false);
  }

  // Store Page Revamp

  getIsStoreV2Enabled(): boolean {
    return Preferences.getBoolClientProperty(STORE_V2_ENABLED, false);
  }

  // Store V2 Deposit Slate Color Variant
  getStoreV2DepositSlateVariant(): number {
    return Preferences.getIntClientProperty(STORE_V2_DEPOSIT_SLATE_VARIANT, 1);
  }

  // Store V2 Bigger Carousel
  getStoreV2BiggerCarouselEnabled(): boolean {
    return Preferences.getBoolClientProperty(STORE_V2_CAROUSEL_BIGGER_ENABLED, false);
  }

  // Store V2 Extra Badge on Carousel
  getStoreV2CarouselExtraValueBadgeEnabled(): boolean {
    return Preferences.getBoolClientProperty(STORE_V2_CAROUSEL_EXTRA_VALUE_BADGE_ENABLED, false);
  }

  // Store V2 Carousel LTO Full Show
  getStoreV2CarouselLTOFullShow(): boolean {
    return Preferences.getBoolClientProperty(STORE_V2_CAROUSEL_LTO_FULL_SHOW, false);
  }

  // Store V2 Carousel Legacy Enabled
  getStoreV2CarouselLegacyEnabled(): boolean {
    return Preferences.getBoolClientProperty(STORE_V2_CAROUSEL_LEGACY_ENABLED, false);
  }

  // Store V2 Deposit Success Enabled
  getStoreV2DepositSuccessEnabled(): boolean {
    return Preferences.getBoolClientProperty(STORE_V2_DEPOSIT_SUCCESS_ENABLED, false);
  }

  // Store V2 Deposit Success Recommended Tournament
  getStoreV2DepositSuccessRecommendedTournament(): number {
    return Preferences.getIntClientProperty(STORE_V2_DEPOSIT_SUCCESS_RECOMMENDED_TOURNAMENT, 1);
  }

  //Insufficient Funds Modal
  getInsuffcientFundsDepositEnabled(): boolean {
    return Preferences.getBoolClientProperty(this.OUT_OF_FUNDS_DEPOSIT_MODAL, false);
  }

  // Low Z Balance LTO
  getLowZBalanceLtoEnabled(): boolean {
    return Preferences.getBoolClientProperty(this.LOW_Z_BALANCE_LTO_ENABLED, false);
  }

  getLowZBalanceLtoId(): string | null {
    return Preferences.getClientProperty(this.LOW_Z_BALANCE_LTO_ID) || null;
  }

  // Trophy
  getTrophiesPageVersion(): string {
    return (
      Preferences.getClientProperty(this.TROPHIES_PAGE_VERSION) || SKZConstants.SHOW_NATIVE_TROPHIES
    );
  }

  //Account Menu
  getPushNotificationsEnabled(): boolean {
    return Preferences.getBoolClientProperty(this.PUSH_NOTIFICATIONS_SETTINGS_ENABLED, false);
  }

  getAccountDeletionEnabled(): boolean {
    return Preferences.getBoolClientProperty(this.ACCOUNT_DELETION_ENABLED, false);
  }

  getVerbiageForAccountDeletion(): string {
    return (
      Preferences.getClientProperty(this.ACCOUNT_DELETION_MODAL_COPY) ||
      _m(
        'Please confirm if you would like to proceed with the deletion of your account. Pending an account audit, we will refund you any remaining real cash balance. Please note it may take up to 30 days for your request to be fully processed.'
      )
    );
  }

  // 3rd party ads methods
  get3rdPartyAdsEnabled(): boolean {
    return this.getInterstitialsEnabled()
      ? Preferences.getBoolClientProperty(this.THIRD_PARTY_ADS_ENABLED_WITH_INTERSTITIALS, false)
      : Preferences.getBoolClientProperty(this.THIRD_PARTY_ADS_ENABLED, false);
  }

  get3rdPartyAdsDoubleWinningRecoverLossModalReset(): number {
    return Preferences.getIntClientProperty(
      this.THIRD_PARTY_ADS_DOUBLE_WINNING_RECOVER_LOSS_MODAL_RESET,
      1
    );
  }

  get3rdPartyAdsWithRewards(): number {
    return Preferences.getIntClientProperty(this.THIRD_PARTY_ADS_WITH_REWARD, 1);
  }

  get3rdPartyAdsWithoutRewards(): number {
    return Preferences.getIntClientProperty(this.THIRD_PARTY_ADS_WITHOUT_REWARD, 2);
  }

  get3rdPartyAdsHouseAdsPercentage(): number {
    return Preferences.getIntClientProperty(this.THIRD_PARTY_ADS_HOUSE_ADS_PERCENTAGE, 0);
  }

  getStoreClaimzEnabled(): boolean {
    return Preferences.getBoolClientProperty(this.STORE_CLAIMZ_ENABLED, true);
  }

  // Checkout Details

  getCheckoutDetailsShowBonus(): boolean {
    return Preferences.getBoolClientProperty(this.CHECKOUT_DETAILS_SHOW_BONUS, true);
  }

  getCheckoutDetailsShowNewBalance(): boolean {
    return Preferences.getBoolClientProperty(this.CHECKOUT_DETAILS_SHOW_NEW_BALANCE, false);
  }

  getCheckoutBillingAddressRequired(): boolean {
    return Preferences.getBoolClientProperty(this.CHECKOUT_BILLING_ADDRESS_REQUIRED, false);
  }

  getCheckoutDefaultApplePay(): boolean {
    return Preferences.getBoolClientProperty(this.CHECKOUT_DEFAULT_APPLEPAY, false);
  }

  getCheckoutEnableCreditCardScan(): boolean {
    return Preferences.getBoolClientProperty(this.CHECKOUT_ENABLE_CREDITCARD_SCAN, false);
  }

  getCheckoutV2PayPalUseBraintree(): boolean {
    return Preferences.getBoolClientProperty(this.CHECKOUT_V2_PAYPAL_USE_BRAINTREE, false);
  }

  getIsEprotectEnabled(): boolean {
    return Preferences.getBoolClientProperty(this.PCI_EPROTECT_ENABLED, false);
  }

  getCanIgnoreThreatMetrixFailure(): boolean {
    return Preferences.getBoolClientProperty(this.PCI_TRANSACT_WITHOUT_THREATMETRIX, false);
  }

  getIsVenmoEnabled(): boolean {
    return Preferences.getBoolClientProperty(this.CHECKOUT_VENMO_ENABLED, false);
  }

  getIsCheckoutVenmoBannerEnabled(): boolean {
    return Preferences.getBoolClientProperty(this.CHECKOUT_VENMO_BANNER, false);
  }

  getIsCheckoutVenmoModalEnabled(): boolean {
    return Preferences.getBoolClientProperty(this.CHECKOUT_VENMO_MODAL, false);
  }

  getIsVenmoHighlightEnabled(): boolean {
    return Preferences.getBoolClientProperty(this.CHECKOUT_VENMO_ENABLED_HIGHLIGHTED, false);
  }

  getIsACHDepositEnabled(): boolean {
    return Preferences.getBoolClientProperty(this.CHECKOUT_ACH_DEPOSIT_ENABLED, false);
  }

  getIsCheckoutEnabledWithPendingWithdrawal(): boolean {
    return Preferences.getBoolClientProperty(this.CHECKOUT_ALLOW_DEPOSIT_DURING_WITHDRAWAL, false);
  }

  getIsCheckoutACHSecurityMessagingEnabled(): boolean {
    return Preferences.getBoolClientProperty(this.CHECKOUT_ACH_SECURITY_MESSAGING_ENABLED, false);
  }

  // LTO
  getAddFundsLTOModalType(): string {
    const property = Preferences.getClientProperty(this.ADDFUNDS_LTO_MODAL);
    if (property === null || !supportedLTOModals.includes(property)) {
      return 'NEW';
    } else {
      return property;
    }
  }

  getIsLTOOnPlay(): boolean {
    return Preferences.getBoolClientProperty(this.LTO_ON_PLAY, false);
  }

  getIsLTOOnStore(): boolean {
    return Preferences.getBoolClientProperty(this.LTO_ON_STORE, true);
  }

  // Tiers
  getProgressionModalEnabled(): boolean {
    // https://skillzinc.atlassian.net/browse/PS-35765 - re-enable when we re-enable tier progression
    return false; // Preferences.getBoolClientProperty(this.PROGRESSION_MODAL_ENABLED, true);
  }

  // Loyalty Store
  getLoyaltyStoreEnabled(): boolean {
    return Preferences.getBoolClientProperty(this.LOYALTY_ENABLED, false);
  }

  getSkillzRewardsCellEnabled(): boolean {
    return Preferences.getBoolClientProperty(this.SKILLZ_REWARDS_CELL_ENABLED, false);
  }

  getLoyaltyProfileFramesEnabled(): boolean {
    return Preferences.getBoolClientProperty(LOYALTY_SHOW_PROFILE_FRAMES, false);
  }
  getLoyalyCombinedWelcomeBackEnabled(): boolean {
    return Preferences.getBoolClientProperty(LOYALTY_COMBINED_WELCOME_BACK_ENABLED, false);
  }

  // Ticketz Constants
  getShouldUseScalableImageForTicketzTierIcons(): boolean {
    return Preferences.getBoolClientProperty(this.TICKETZ_TIER_USE_SCALABLE_IMAGE, false);
  }

  getTierBadgesEnabled(): boolean {
    return Preferences.getBoolClientProperty(this.TICKETZ_TIER_BADGES_ENABLED, false);
  }

  // IDFA
  getIDFAPromptFTUESetting(): string {
    return Preferences.getClientProperty(this.IDFA_FTUE_PROMPT_SETTNG);
  }

  getIDFAPromptDepositEnabled(): boolean {
    return Preferences.getBoolClientProperty(this.IDFA_DEPOSIT_PROMPT_ENABLED, false);
  }

  getIDFAPromptFirstOpenPlay(): boolean {
    return Preferences.getBoolClientProperty(this.IDFA_FIRST_OPEN_PLAY, false);
  }

  // Hosted Live Events
  getLiveEventHostedEnabled(): boolean {
    return Preferences.getBoolClientProperty(this.LIVE_EVENT_HOSTED_ENABLED, false);
  }

  // Play Tab Improvement (auto expand of Head to Head Section)
  getAutoExpandProH2HAfterXMatches(): number {
    return Preferences.getIntClientProperty(this.AUTO_EXPAND_PRO_H2H_AFTER_X_MATCHES, -1);
  }

  // Live Tournament Templates Auto-Expansion
  getAutoExpandLiveTournamentTemplatesAfterXMatches(): number {
    return Preferences.getIntClientProperty(
      this.AUTO_EXPAND_LIVE_TOURNAMENT_TEMPLATES_AFTER_X_MATCHES,
      -1
    );
  }

  // Progression
  getDevProgressionHomeScreenEnabled(): boolean {
    return Preferences.getBoolClientProperty(this.DEV_PROGRESSION_HOME_ENABLED, false);
  }

  getDevProgressionSidebarEnabled(): boolean {
    return Preferences.getBoolClientProperty(this.DEV_PROGRESSION_SIDEBAR_ENABLED, false);
  }

  getFtueWebTutorialScreenEnabled(): boolean {
    return Preferences.getBoolClientProperty(this.FTUE_WEB_TUTORIAL_SCREEN_ENABLED, false);
  }

  getFtueCurrencyPromptEnabled(): boolean {
    return Preferences.getBoolClientProperty(this.FTUE_CURRENCY_PROMPT_ENABLED, false);
  }

  getWelcomeBackV3Enabled(): boolean {
    return Preferences.getBoolClientProperty(this.WELCOME_BACK_V3_ENABLED, false);
  }

  //Leagues
  getLeaguesVersion(): string {
    return Preferences.getClientProperty(this.LEAGUES_VERSION) || 'v3.0';
  }

  getLeaguesV3Enabled(): boolean {
    return this.getLeaguesVersion() == 'v3.0';
  }

  getLeaguesToastEnabled(): boolean {
    return Preferences.getBoolClientProperty(this.LEAGUES_TOAST_ENABLED, false);
  }

  getLeaguesNegativeToastEnabled(): boolean {
    return Preferences.getBoolClientProperty(this.LEAGUES_NEGATIVE_TOAST_ENABLED, false);
  }

  getLeaguesCombinedMedalsEnabled(): boolean {
    return (
      Preferences.getBoolClientProperty(this.LEAGUES_COMBINED_MEDALS_ENABLED, false) &&
      Preferences.getBoolClientProperty(this.LEAGUES_V3_MEDAL_LEADERBOARD_ENABLED, false)
    );
  }

  getLeaguesLockedFTUEV2Enabled(): boolean {
    return Preferences.getBoolClientProperty(this.LEAGUES_LOCKED_FTUE_V2_ENABLED, false);
  }

  getLeaguesStatusInterstitialEnabled(): boolean {
    return Preferences.getBoolClientProperty(LEAGUES_STATUS_INTERSTITIAL_ENABLED, false);
  }

  getLeaguesStatusInterstitialMaxSkips(): number {
    return Preferences.getIntClientProperty(LEAGUES_STATUS_INTERSTITIAL_MAX_SKIPS, 5);
  }

  getLeaguesManualClaimEnabled(): boolean {
    return Preferences.getBoolClientProperty(LEAGUES_MANUAL_CLAIM_ENABLED, false);
  }

  getH2HResultsExperimentEnabled(): boolean {
    return Preferences.getBoolClientProperty(H2H_RESULTS_EXPERIMENT_ENABLED, false);
  }
  getH2HResultsAddBalanceCTAEnabled(): boolean {
    return Preferences.getBoolClientProperty(H2H_RESULTS_ADD_BALANCE_CTA_ENABLED, false);
  }

  getH2HResultsHugeWinEnabled(): boolean {
    return Preferences.getBoolClientProperty(H2H_RESULTS_HUGE_WIN_ENABLED, false);
  }

  getH2HResultsMarginalLossEnabled(): boolean {
    return Preferences.getBoolClientProperty(H2H_RESULTS_MARGINAL_LOSS_ENABLED, false);
  }

  getH2HResultsTrophiesEnabled(): boolean {
    return Preferences.getBoolClientProperty(H2H_RESULTS_TROPHIES_ENABLED, false);
  }

  getH2HResultsSpectateEnabled(): boolean {
    return Preferences.getBoolClientProperty(H2H_RESULTS_SPECTATE_ENABLED, false);
  }

  getH2HResultsRematchExperimentEnabled(): boolean {
    return Preferences.getBoolClientProperty(H2H_RESULTS_REMATCH_EXPERIMENT_ENABLED, false);
  }

  getH2HResultsRecommendedTournamentExperimentEnabled(): boolean {
    return Preferences.getBoolClientProperty(H2H_RESULTS_RECOMMENDED_TOURNAMENT_ENABLED, false);
  }

  getH2HResultsRecommendedTournamentEnabledCount(): number {
    return Preferences.getIntClientProperty(H2H_RESULTS_RECOMMENDED_TOURNAMENT_ENABLED_COUNT, 3);
  }

  isH2HMaxEligibleEntryFeeEnabled(): boolean {
    return Preferences.getBoolClientProperty(ENABLE_H2H_MAX_ELIGIBLE_ENTRY_FEE, false);
  }

  getH2HMaxEligibleEntryFeeCeiling(): number {
    const value = Preferences.getClientProperty(H2H_MAX_ELIGIBLE_ENTRY_FEE_CEILING);
    return value ? parseFloat(String(value)) : 9999.0;
  }

  getLeaguesPaidLeaguePromptCooldownMinutes(): number {
    return Preferences.getIntClientProperty(LEAGUES_PAID_LEAGUE_PROMPT_COOLDOWN_MINUTES, 1440);
  }

  getLeaguesPaidLeaguePromptMaxTriggersSeason(): number {
    return Preferences.getIntClientProperty(LEAGUES_PAID_LEAGUE_PROMPT_MAX_TRIGGERS_SEASON, 5);
  }

  getLeaguesPaidLeagueOptInCutoffMin(): number {
    return Preferences.getIntClientProperty(LEAGUES_PAID_LEAGUE_OPT_IN_CUTOFF_MIN, 30);
  }

  getLeaguesV3UIV2Enabled(): boolean {
    return Preferences.getBoolClientProperty(LEAGUES_V3_UI_V2_ENABLED, false);
  }

  // Match Select Screen
  getIsShowFTUECashMatches(): boolean {
    return Preferences.getBoolClientProperty(this.IS_SHOW_FTUE_CASH_MATCHES, false);
  }

  getIsAsyncFtueBotEnabled(): boolean {
    return Preferences.getBoolClientProperty(this.IS_ASYNC_FTUE_BOT_ENABLED, false);
  }

  // X Button Experiment
  getResultNextButtonEnabled(): boolean {
    return Preferences.getBoolClientProperty(this.RESULT_NEXT_BUTTON_ENABLED, true);
  }

  //SMS Verification
  getVerifySMSGameplay(): boolean {
    return Preferences.getBoolClientProperty(this.VERIFY_SMS_GAMEPLAY, false);
  }

  getVerifySMSDeposit(): boolean {
    return Preferences.getBoolClientProperty(this.VERIFY_SMS_DEPOSIT, false);
  }

  getVerifySMSSaveAccount(): boolean {
    return Preferences.getBoolClientProperty(this.VERIFY_SMS_SAVEACCOUNT, false);
  }

  getVerifySMSCloseButton(): boolean {
    return Preferences.getBoolClientProperty(this.VERIFY_SMS_CLOSE_BUTTON, true);
  }

  // NEW SMS Verification
  getSMSPromptMaxPrompts(): number {
    return Preferences.getIntClientProperty(SMS_PROMPT_MAX_PROMPTS, 6);
  }

  getSMSPromptDeposit(): string {
    return Preferences.getClientProperty(SMS_PROMPT_DEPOSIT) || 'donotprompt';
  }

  getSMSPromptDepositHeader(): string {
    return (
      Preferences.getClientProperty(SMS_PROMPT_DEPOSIT_HEADER) || _m('Verify your phone number')
    );
  }

  getSMSPromptDepositBody(): string {
    return (
      Preferences.getClientProperty(SMS_PROMPT_DEPOSIT_BODY) ||
      _m('This extra layer of security helps prevent unauthorized access to your account.')
    );
  }

  getSMSPromptWithdraw(): string {
    return Preferences.getClientProperty(SMS_PROMPT_WITHDRAW) || 'required';
  }

  getSMSPromptWithdrawHeader(): string {
    return (
      Preferences.getClientProperty(SMS_PROMPT_WITHDRAW_HEADER) || _m('Verify your phone number')
    );
  }

  getSMSPromptWithdrawBody(): string {
    return (
      Preferences.getClientProperty(SMS_PROMPT_WITHDRAW_BODY) ||
      _m('Verify your phone number to withdraw funds.')
    );
  }

  getSMSSaveAccount(): boolean {
    return Preferences.getBoolClientProperty(SMS_SAVE_ACCOUNT) || false;
  }

  //Federated Login Constants
  getGoogleLoginEnabledIOS(): boolean {
    return Preferences.getBoolClientProperty(this.FEDLOGIN_GOOGLE_IOS_LOGIN, false);
  }

  getGoogleLoginEnabledAndroid(): boolean {
    return Preferences.getBoolClientProperty(this.FEDLOGIN_GOOGLE_ANDROID_LOGIN, false);
  }

  getAppleLoginEnabledIOS(): boolean {
    return Preferences.getBoolClientProperty(this.FEDLOGIN_APPLE_IOS_LOGIN, false);
  }

  getAppleLoginEnabledAndroid(): boolean {
    return Preferences.getBoolClientProperty(this.FEDLOGIN_APPLE_ANDROID_LOGIN, false);
  }

  getFacebookLoginEnabledIOS(): boolean {
    return Preferences.getBoolClientProperty(this.FEDLOGIN_FACEBOOK_IOS_LOGIN, false);
  }

  getFacebookLoginEnabledAndroid(): boolean {
    return Preferences.getBoolClientProperty(this.FEDLOGIN_FACEBOOK_ANDROID_LOGIN, false);
  }

  //Federated Sign Up Constants
  getGoogleSignUpEnabledIOS(): boolean {
    return Preferences.getBoolClientProperty(this.FEDLOGIN_GOOGLE_IOS_SIGNUP, false);
  }

  getGoogleSignUpEnabledAndroid(): boolean {
    return Preferences.getBoolClientProperty(this.FEDLOGIN_GOOGLE_ANDROID_SIGNUP, false);
  }

  getAppleSignUpEnabledIOS(): boolean {
    return Preferences.getBoolClientProperty(this.FEDLOGIN_APPLE_IOS_SIGNUP, false);
  }

  getAppleSignUpEnabledAndroid(): boolean {
    return Preferences.getBoolClientProperty(this.FEDLOGIN_APPLE_ANDROID_SIGNUP, false);
  }

  getFacebookSignUpEnabledIOS(): boolean {
    return Preferences.getBoolClientProperty(this.FEDLOGIN_FACEBOOK_IOS_SIGNUP, false);
  }

  getFacebookSignUpEnabledAndroid(): boolean {
    return Preferences.getBoolClientProperty(this.FEDLOGIN_FACEBOOK_ANDROID_SIGNUP, false);
  }

  getRUMEnabled(): boolean {
    return Preferences.getBoolClientProperty(this.RUM_ENABLED, false);
  }

  getIsErrorHandlerEnabled(): boolean {
    return Preferences.getBoolClientProperty(this.IS_ERROR_HANDLER_ENABLED, true);
  }

  getIsErrorBoundaryEnabled(): boolean {
    return Preferences.getBoolClientProperty(this.IS_ERROR_BOUNDARY_ENABLED, true);
  }

  getRUMSessionSampleRate(): number {
    return Preferences.getIntClientProperty(this.RUM_SESSION_SAMPLE_RATE, 0);
  }
  getRUMResourceSampleRate(): number {
    return Preferences.getIntClientProperty(this.RUM_RESOURCE_SAMPLE_RATE, 0);
  }

  getRUMCrashDetectionEnabled(): boolean {
    return Preferences.getBoolClientProperty(this.RUM_NATIVE_CRASH_DETECTION, false);
  }

  // Enable/Disable SMS/Email Sign-In
  getSSOLoginSMSEnabled(): boolean {
    return Preferences.getBoolClientProperty(this.SSO_LOGIN_SMS_ENABLED, true);
  }

  getSSOLoginEmailEnabled(): boolean {
    return Preferences.getBoolClientProperty(this.SSO_LOGIN_EMAIL_ENABLED, true);
  }

  // Default Sign-In Constant
  getSSOLoginEmailDefault(): boolean {
    return Preferences.getBoolClientProperty(this.SSO_LOGIN_EMAIL_DEFAULT, false);
  }

  // Core Loop Tungsten AB Testing
  isCoreLoopTungstenEnabled(): boolean {
    return Preferences.getBoolClientProperty(this.CORELOOP_TUNGSTEN_ENABLED, false);
  }

  isCoreLoopTungstenLiteEnabled(): boolean {
    return Preferences.getBoolClientProperty(this.CORELOOP_TUNGSTEN_LITE_ENABLED, false);
  }

  //Spectate
  getSpectateEnabledResults(): Boolean {
    return Preferences.getBoolClientProperty(this.SPECTATE_ENABLED_RESULTS, false);
  }

  // Cash Game Registration
  getSkipCashGameFullRegCheck(): boolean {
    return Preferences.getBoolClientProperty(this.SKIP_CASH_GAME_FULL_REG_CHECK, false);
  }

  //Instant Match
  getInstantMatchEnabled(): boolean {
    return Preferences.getBoolClientProperty(INSTANT_MATCH_ENABLED, false);
  }

  getInstantMatchEntryFeeBand(): string {
    return (
      Preferences.getClientProperty(INSTANT_MATCH_ENTRY_FEE_BAND) || SKZConstants.PlayerSegment.NONE
    );
  }

  getInstantMatchEntryFeeValue(): string {
    return Preferences.getClientProperty(INSTANT_MATCH_ENTRY_FEE_VALUE) || '0';
  }

  getEntryFeeThresholdToDisplayEntryFeeDiscount(): number {
    return Preferences.getIntClientProperty(INSTANT_MATCH_ENTRY_FEE_THRESHOLD, 0);
  }

  getClubzEnabled(): boolean {
    return Preferences.getBoolClientProperty(CLUBZ_ENABLED, false);
  }

  // FTUE Revamp
  getFTUERevampEnabled(): boolean {
    return Preferences.getBoolClientProperty(this.FTUE_REVAMP_ENABLED, false);
  }

  getAvatarRandomizerEnabled(): boolean {
    return Preferences.getBoolClientProperty(this.AVATAR_RANDOMIZER_ENABLED, false);
  }

  getAvatarRandomizerOnlyFTUE(): boolean {
    return Preferences.getBoolClientProperty(this.AVATAR_RANDOMIZER_ONLY_FTUE, false);
  }

  getUsernameRandomizerEnabled(): boolean {
    return Preferences.getBoolClientProperty(this.USERNAME_RANDOMIZER_ENABLED, false);
  }

  getCompressedBirthdayOptInEnabled(): boolean {
    return Preferences.getBoolClientProperty(this.COMPRESSED_BIRTHDAY_OPT_IN, false);
  }

  getEventChatEnabled(): boolean {
    return (
      Preferences.getBoolClientProperty(this.EVENT_CHAT_ENABLED, false) &&
      this.getIsPlayerChatEnabled()
    );
  }

  getLiveBracketEventChatEnabled(): boolean {
    return (
      Preferences.getBoolClientProperty(this.LIVE_BRACKET_EVENT_CHAT_ENABLED, false) &&
      this.getIsPlayerChatEnabled()
    );
  }

  getLiveBracketPreventJoinSecs(): number {
    return parseInt(
      Preferences.getClientProperty(this.LIVE_BRACKET_PREVENT_JOIN_SECS) || '600',
      10
    );
  }

  getShowMoreTextButtonsEnabled(): boolean {
    return Preferences.getBoolClientProperty(this.SHOW_MORE_TEXT_BUTTONS_ENABLED, false);
  }

  getTournamentTemplateUIV2Enabled(): boolean {
    return Preferences.getBoolClientProperty(TOURNAMENT_TEMPLATE_UI_V2_ENABLED, false);
  }

  // Recommended Templates v1
  getRecommendedTemplatesEnabled(): boolean {
    return Preferences.getBoolClientProperty(RECOMMENDED_TEMPLATES_ENABLED, false);
  }

  getRecommendedTemplateEntryFeeValue(): string {
    return Preferences.getClientProperty(RECOMMENDED_TEMPLATE_ENTRY_FEE_VALUE) || '0';
  }

  getRecommendedSlot1BadgeActive(): boolean {
    return Preferences.getBoolClientProperty(RECOMMENDED_SLOT1_BADGE_ACTIVE, false);
  }

  getRecommendedSlot1BadgeText(): string {
    return Preferences.getClientProperty(RECOMMENDED_SLOT1_BADGE_TEXT) || '';
  }

  getRecommendedSlot1Recommendation(): string {
    return (
      Preferences.getClientProperty(RECOMMENDED_SLOT1_RECOMMENDATION) ||
      SKZConstants.RecommendedTemplateConditions.NONE
    );
  }

  getRecommendedSlot2BadgeActive(): boolean {
    return Preferences.getBoolClientProperty(RECOMMENDED_SLOT2_BADGE_ACTIVE, false);
  }

  getRecommendedSlot2BadgeText(): string {
    return Preferences.getClientProperty(RECOMMENDED_SLOT2_BADGE_TEXT) || '';
  }

  getRecommendedSlot2Recommendation(): string {
    return (
      Preferences.getClientProperty(RECOMMENDED_SLOT2_RECOMMENDATION) ||
      SKZConstants.RecommendedTemplateConditions.NONE
    );
  }

  getRecommendedSlot3BadgeActive(): boolean {
    return Preferences.getBoolClientProperty(RECOMMENDED_SLOT3_BADGE_ACTIVE, false);
  }

  getRecommendedSlot3BadgeText(): string {
    return Preferences.getClientProperty(RECOMMENDED_SLOT3_BADGE_TEXT) || '';
  }

  getRecommendedSlot3Recommendation(): string {
    return (
      Preferences.getClientProperty(RECOMMENDED_SLOT3_RECOMMENDATION) ||
      SKZConstants.RecommendedTemplateConditions.NONE
    );
  }

  // Recommended Templates v2
  getRecommendedTemplatesV2UIenabled(): boolean {
    return Preferences.getBoolClientProperty(RECOMMENDED_TEMPLATES_V2_UI_ENABLED, false);
  }

  getRecommendedTemplateV2EntryFeeValue(): string {
    return Preferences.getClientProperty(RECOMMENDED_TEMPLATES_V2_ENTRY_FEE_VALUE) || '0';
  }

  getRecommendedTemplateV2LiveEventEnabled(): boolean {
    return Preferences.getBoolClientProperty(RECOMMENDED_TEMPLATES_V2_LIVE_EVENT_ENABLED, false);
  }

  getRecommendedTemplateV2AscendingEnabled(): boolean {
    return Preferences.getBoolClientProperty(RECOMMENDED_TEMPLATES_V2_ASCENDING_ENABLED, true);
  }

  getRecommendedTemplateV2Slot1BadgeText(): string {
    return Preferences.getClientProperty(RECOMMENDED_TEMPLATES_V2_SLOT1_BADGE_TEXT) || '';
  }

  getRecommendedTemplateV2Slot2BadgeText(): string {
    return Preferences.getClientProperty(RECOMMENDED_TEMPLATES_V2_SLOT2_BADGE_TEXT) || '';
  }

  getRecommendedTemplateV2Slot3BadgeText(): string {
    return Preferences.getClientProperty(RECOMMENDED_TEMPLATES_V2_SLOT3_BADGE_TEXT) || '';
  }

  getFTUEConversionsEnabled(): boolean {
    return Preferences.getBoolClientProperty(this.FTUE_CONVERSIONS_ENABLED, false);
  }

  getTutorialTrainersImageSet(): Record<string, string> {
    const jsonString = Preferences.getClientProperty(this.TUTORIAL_TRAINERS_IMAGE_SET);
    let imageSet = {};
    try {
      if (jsonString) {
        imageSet = JSON.parse(jsonString);
      }
    } catch {
      console.warn('Failed to parse tutorial trainers image set', jsonString);
    }
    return imageSet;
  }

  // ABUSE PREVENTION
  getLogoutEnabled(): boolean {
    return Preferences.getBoolClientProperty(LOGOUT_ENABLED, false);
  }

  getBonusCashRestrictionUIV1Enabled(): boolean {
    return Preferences.getBoolClientProperty(BONUS_CASH_RESTRICTION_UI_V1_ENABLED, false);
  }

  getLoyaltyRevampUIV2Enabled(): boolean {
    return Preferences.getBoolClientProperty(LOYALTY_REVAMP_UI_ENABLED, false);
  }

  getVipContactNumber(): string {
    return Preferences.getClientProperty(VIP_CONTACT_NUMBER) || '';
  }

  getVipContactAgentName(): string {
    return Preferences.getClientProperty(VIP_CONTACT_AGENT_NAME) || '';
  }

  getVipContactEmail(): string {
    return Preferences.getClientProperty(VIP_CONTACT_EMAIL) || '';
  }

  getVipContactVCFURL(): string {
    return Preferences.getClientProperty(VIP_CONTACT_VCF_URL) || '';
  }

  getVipContactWhatsappURL(): string {
    return Preferences.getClientProperty(VIP_CONTACT_WHATSAPP_URL) || '';
  }

  // MIGRATE USER PROPERTIES
  getMigrateUserEnabled(): boolean {
    return Preferences.getBoolClientProperty(MIGRATE_USER_ENABLED, false);
  }

  getMigrateBannerURL(): string {
    return Preferences.getClientProperty(MIGRATE_BANNER_URL) || '';
  }

  getMigrateIOSURL(): string {
    return Preferences.getClientProperty(MIGRATE_IOS_URL) || '';
  }

  getMigrateAndroidURL(): string {
    return Preferences.getClientProperty(MIGRATE_ANDROID_URL) || '';
  }

  // Live Brackets
  getTournamentTimerLengthSeconds(): number {
    return parseInt(
      Preferences.getClientProperty(this.TOURNAMENT_TIMER_LENGTH_SECONDS) || '120',
      10
    );
  }

  getApplePayEnabled(): boolean {
    return Preferences.getBoolClientProperty(APPLE_PAY_ENABLED, false);
  }

  // Helper to normalize "0" values to null (treat "0" as empty/disabled)
  private _normalizeBannerValue(value: string | null): string | null {
    if (!value || value.trim() === '' || value.trim() === '0') {
      return null;
    }
    return value;
  }

  // Common banner parsing logic
  private _parseBannerConfig(
    gameIds: string | null,
    targetUrls: string | null,
    imageUrls: string | null,
    positions: string | null,
    defaultGameIdPrefix: string,
    errorPrefix: string
  ): any {
    if (Platform.OS === 'web') {
      return null;
    }
    // Normalize values: treat "0" as null/empty
    const normalizedTargetUrls = this._normalizeBannerValue(targetUrls);
    const normalizedImageUrls = this._normalizeBannerValue(imageUrls);
    const normalizedPositions = this._normalizeBannerValue(positions);

    // If any of the required properties are missing or "0", return null
    if (!normalizedTargetUrls || !normalizedImageUrls || !normalizedPositions) {
      return null;
    }

    try {
      // Normalize gameIds (optional field)
      const normalizedGameIds = this._normalizeBannerValue(gameIds);

      // Split comma-separated values
      const gameIdArray = normalizedGameIds
        ? normalizedGameIds.split(',').map((id: string) => id.trim())
        : [];
      const targetUrlArray = normalizedTargetUrls.split(',').map((url: string) => url.trim());
      const imageUrlArray = normalizedImageUrls.split(',').map((url: string) => url.trim());
      const positionArray = normalizedPositions.split(',').map((pos: string) => pos.trim());

      // Ensure all arrays have the same length (use the shortest length)
      const length = Math.min(targetUrlArray.length, imageUrlArray.length, positionArray.length);

      // Build banners array
      const banners = [];
      for (let i = 0; i < length; i++) {
        const banner = {
          gameId: gameIdArray[i] || `${defaultGameIdPrefix}-${i}`,
          targetUrl: targetUrlArray[i],
          imageUrl: imageUrlArray[i],
          position: positionArray[i],
        };
        banners.push(banner);
      }

      // Filter out banners where gameId matches current game and URL is external
      const currentGameId = SkillzModule?.App?.GameId;

      let filteredBanners = banners.filter((banner) => {
        const isInternalDeepLink = banner.targetUrl?.startsWith('skillz://') || false;
        const isSameGame = banner.gameId === String(currentGameId);

        // Keep banner if:
        // - It's an internal deep link (always show these)
        // - OR it's for a different game
        // Filter out if: same game AND external URL
        const shouldKeep = isInternalDeepLink || !isSameGame;

        return shouldKeep;
      });
      // Filter out external banners if client property disables them
      if (!this.shouldShowExternalBanners()) {
        filteredBanners = filteredBanners.filter((banner) => {
          // Keep only banners with internal deep links (starts with skillz://)
          return banner.targetUrl?.startsWith('skillz://') || false;
        });
      }

      return {banners: filteredBanners};
    } catch (error) {
      console.error(`[${errorPrefix}] ❌ Failed to parse banners config:`, error);
      console.error(
        `[${errorPrefix}] 📍 Error stack:`,
        error instanceof Error ? error.stack : 'No stack trace'
      );
      return null;
    }
  }

  // Lobby/PlayTab Banners
  getPlayTabBannersConfig(): any {
    const gameIds = Preferences.getClientProperty(BANNER_LOBBY_GAME_IDS);
    const targetUrls = Preferences.getClientProperty(BANNER_LOBBY_TARGET_URLS);
    const imageUrls = Preferences.getClientProperty(BANNER_LOBBY_IMAGE_URLS);
    const positions = Preferences.getClientProperty(BANNER_LOBBY_POSITIONS);

    return this._parseBannerConfig(
      gameIds,
      targetUrls,
      imageUrls,
      positions,
      'home-banner',
      'HOME_BANNER'
    );
  }

  // History Screen Banners
  getHistoryBannersConfig(): any {
    const gameIds = Preferences.getClientProperty(BANNER_HISTORY_GAME_IDS);
    const targetUrls = Preferences.getClientProperty(BANNER_HISTORY_TARGET_URLS);
    const imageUrls = Preferences.getClientProperty(BANNER_HISTORY_IMAGE_URLS);
    const positions = Preferences.getClientProperty(BANNER_HISTORY_POSITIONS);

    return this._parseBannerConfig(
      gameIds,
      targetUrls,
      imageUrls,
      positions,
      'history-banner',
      'HISTORY_BANNER'
    );
  }
  // Banner External URL Control
  shouldShowExternalBanners(): boolean {
    return Preferences.getBoolClientProperty(SHOW_BANNER_EXTERNAL, false);
  }

  // FTUE 2.0
  isFTUEv3Enabled(): boolean {
    if (Platform.OS === 'web') {
      return false;
    }
    return Preferences.getBoolClientProperty(FTUE_V3_ENABLED, false);
  }

  getFTUEv3SignUpPath(): FTUEv3SignUpPathType {
    return Preferences.getClientProperty(FTUE_V3_SIGNUP_PATH) as FTUEv3SignUpPathType;
  }

  ftueV3InterstitialsSkipInitialLoad(): boolean {
    return Preferences.getBoolClientProperty(FTUE_V3_INTERSTITIALS_SKIP_INITIAL_LOAD, false);
  }

  // Maintenance
  getIsMaintenanceInProgress(): boolean {
    return Preferences.getBoolClientProperty(MAINTENANCE_IN_PROGRESS, false);
  }

  getIsPreMaintenance(): boolean {
    return Preferences.getBoolClientProperty(PRE_MAINTENANCE, false);
  }

  getMaintenancePopupHeading(): string {
    return Preferences.getClientProperty(MAINTENANCE_POPUP_HEADING) || 'Under Maintenance';
  }

  getMaintenancePopupSubheading(): string {
    return Preferences.getClientProperty(MAINTENANCE_POPUP_SUBHEADING) || 'Back shortly.';
  }
}

export default new ClientPropertyConstants();
